import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import styled, { createGlobalStyle } from 'styled-components'
import ReactDatePicker from 'react-datepicker'
import { Button, Checkbox, Form, Input, Modal, Radio, TextArea } from 'semantic-ui-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { FormDropdownField } from 'elements'
import { FileUploadPicker, FlexRowBetween } from 'components'
import { Eye, Plus, X } from 'phosphor-react'
import { CustomePreview } from '../inspection/Elements'
import {
    AcceptFileType,
    ControlType,
    getMediaMergeKey,
    getQuestionMediaList,
    mapInspectionQuestionMediaFiles,
    mergeLocalMediaState,
} from 'utility'
import {
    COMPLETION_FAQ,
    CUSTOMER_TYPE_RADIO_OPTIONS,
    filterSupplierDropdownOptions,
    filterSupplierOptionsByClientType,
    supplierOptionsHaveClientType,
    claimExtraAnswerKey,
    findQuestionByAliases,
    findSupplierOptionByValue,
    formatSupplierCityStateZip,
    getClientTypeFromOptionMeta,
    isClaimPortalSection,
    listUnmatchedSectionQuestions,
    PORTAL_FAQ,
    QUALITY_FAQ,
    questionToDropdownOptions,
    questionAllowsOtherOption,
    isClaimMultipleChoiceQuestion,
    readQuestionDisplayValue,
    sortAuditSectionsForClaim,
} from './customer-claim-inspection-utils'
import {
    ClaimNumberBanner,
    ClaimSectionStatusBadge,
    ClaimSectionWorkflowBody,
    ClaimSectionWorkflowFooter,
    isSectionWorkflowFieldsDisabled,
    resolveSectionWorkflowStatus,
    useClaimSectionWorkflowModal,
} from './ClaimSectionWorkflow'

const PageWrap = styled.div`
    margin: -1rem;
    min-height: calc(100vh - 55px);
    background: #eef1f5;
    font-family: inherit;
    overflow: visible;
    /* Room to scroll the last section buttons above the fixed WhatsApp and reCAPTCHA. */
    padding-bottom: 96px;
`

const TopHeader = styled.div`
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
    padding: 0.65rem 1.25rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
`

const HeaderTitle = styled.h1`
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.55rem;
`

const HeaderClaimNumber = styled.span`
    font-size: 0.92rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    padding: 0.15rem 0.55rem;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.45);
`

const HeaderMeta = styled.span`
    font-size: 0.85rem;
    opacity: 0.9;
`

const Content = styled.div`
    padding: 1rem 1.25rem 1.25rem;
    max-width: 1200px;
    margin: 0 auto;
    overflow: visible;
`

const Card = styled.section`
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(26, 43, 75, 0.08);
    margin-bottom: 1rem;
    overflow: visible;
    position: relative;

    &:focus-within {
        z-index: 100;
    }
`

const CardHeader = styled.div`
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
    padding: 0.65rem 1rem;
    min-height: 2.75rem;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    font-weight: 700;
    font-size: 0.95rem;
    border-radius: 8px 8px 0 0;
    overflow: visible;
`

const HeaderActions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
    margin-left: auto;
`

const CardBody = styled.div`
    padding: 1rem 1.25rem;
    overflow: visible;
`

const CopyHeaderButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    background: transparent;
    border: none;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    padding: 0.15rem 0.35rem;
    border-radius: 4px;
    opacity: 0.92;

    &:hover:not(:disabled) {
        opacity: 1;
        background: rgba(255, 255, 255, 0.12);
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`

const FieldGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem 1.25rem;
    width: 100%;
    overflow: visible;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`

/** Grid cell — fields stretch to full column width (matches inspection col layout). */
const GridFieldCell = styled.div`
    width: 100%;
    min-width: 0;
`

/** Full-width inputs inside claim portal forms (datepicker, dropdown, text). */
const ClaimInspectionForm = styled(Form)`
    &&& {
        width: 100%;
    }

    .field {
        width: 100%;
    }

    .ui.input,
    .ui.dropdown,
    textarea {
        width: 100% !important;
        box-sizing: border-box;
    }

    /* Global index.css hides number spinners; .score-input re-enables them */
    .score-input input::-webkit-outer-spin-button,
    .score-input input::-webkit-inner-spin-button {
        -webkit-appearance: inner-spin-button;
        margin: 0;
    }

    .score-input input[type='number'] {
        -moz-appearance: number-input;
    }

    .react-datepicker-wrapper,
    .react-datepicker__input-container {
        width: 100%;
        display: block;
    }

    .react-datepicker__input-container input {
        width: 100% !important;
        box-sizing: border-box;
        padding-left: 2.25rem !important;
    }

    /* Semantic UI fades every disabled control. Keep them full strength. */
    .field :disabled {
        opacity: 1 !important;
    }

    /* Read-only fields stay non-editable, but use the same colors as editable fields. */
    .ui.input input[disabled],
    .ui.input input:disabled,
    .ui.input.disabled input,
    .ui.selection.dropdown.disabled,
    .ui.dropdown.disabled,
    .ui.dropdown.disabled > .text,
    .ui.dropdown.disabled > .default.text,
    textarea[disabled],
    textarea:disabled,
    .react-datepicker__input-container input:disabled {
        opacity: 1 !important;
        color: rgba(0, 0, 0, 0.87) !important;
        -webkit-text-fill-color: rgba(0, 0, 0, 0.87);
        background-color: #fff !important;
        border: 1px solid rgba(34, 36, 38, 0.15) !important;
        cursor: default !important;
    }

    .ui.selection.dropdown.disabled {
        background: #fff !important;
        border: 1px solid rgba(34, 36, 38, 0.15) !important;
        opacity: 1 !important;
    }

    .ui.selection.dropdown.disabled > .dropdown.icon {
        opacity: 1;
    }

    .ui.button.disabled,
    .ui.button:disabled {
        opacity: 1 !important;
        cursor: default !important;
    }

    .ui.blue.button.disabled,
    .ui.blue.button:disabled {
        background-color: #2185d0 !important;
        color: #fff !important;
        -webkit-text-fill-color: #fff;
    }

    .ui.teal.button.disabled,
    .ui.teal.button:disabled {
        background-color: #00b5ad !important;
        color: #fff !important;
        -webkit-text-fill-color: #fff;
    }

    .ui.input input[disabled]::placeholder,
    .ui.input input:disabled::placeholder,
    textarea[disabled]::placeholder,
    textarea:disabled::placeholder,
    .react-datepicker__input-container input:disabled::placeholder {
        color: rgba(191, 191, 191, 0.87) !important;
        -webkit-text-fill-color: rgba(191, 191, 191, 0.87);
        opacity: 1 !important;
    }

    label {
        color: #1f2937;
    }
`

/** Portal mockup: paired rows, with full-width item sketch and complaint. */
const PortalFieldGrid = styled(FieldGrid)`
    grid-template-areas:
        'mattype size'
        'itemsketch itemsketch'
        'quantity sfo'
        'complaint complaint';

    @media (max-width: 768px) {
        grid-template-areas:
            'mattype'
            'size'
            'itemsketch'
            'quantity'
            'sfo'
            'complaint';
    }
`

/** Code onward packs left-right, so a missing RMA does not leave a hole. */
const PortalTailGrid = styled(FieldGrid)`
    margin-top: 1rem;
`

const SelectedSupplierSummary = styled.div`
    margin-top: 0.75rem;
    padding: 0.75rem 1rem;
    background: #e8f6fc;
    border: 1px solid #b8e4f2;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    justify-content: center;
`

const PortalMatType = styled.div`
    grid-area: mattype;
`
const PortalSize = styled.div`
    grid-area: size;
`
const PortalItemSketch = styled.div`
    grid-area: itemsketch;
`
const PortalQuantity = styled.div`
    grid-area: quantity;
`
const PortalSfo = styled.div`
    grid-area: sfo;
`
const PortalComplaint = styled.div`
    grid-area: complaint;
`
const PortalNotesCell = styled(GridFieldCell)`
    overflow: visible;
    z-index: 3;
`

const FullWidth = styled.div`
    grid-column: 1 / -1;
`

const SingleColumnFields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`

const FormFieldLabel = styled.label`
    display: block;
    margin-bottom: 0.35rem;
`

const ItemSketchFieldWrap = styled.div`
    width: 100%;

    .field {
        margin: 0 !important;
    }
`

const ItemSketchRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;

    @media (max-width: 768px) {
        flex-wrap: wrap;
        align-items: stretch;
    }
`

const SketchInputWrap = styled.div`
    flex: 1 1 auto;
    min-width: 0;

    .field {
        margin-bottom: 0 !important;
    }

    .ui.input,
    .ui.selection.dropdown {
        width: 100% !important;
    }
`

/** Match normal inspection template media preview (NotesMediaContainer). */
const ClaimMediaUploadWrap = styled.div`
    width: 100%;
    overflow: visible;

    .flex-wrap.flex-row.mt-4 {
        margin-top: 0.75rem !important;
        gap: 10px;
        justify-content: flex-start !important;
        overflow: visible;
    }

    .preview-media,
    .media-item {
        height: 100px !important;
        width: 100px !important;
        min-width: 100px !important;
        max-width: 100px !important;
        flex: 0 0 100px !important;
        overflow: hidden;

        img {
            width: 100% !important;
            height: 100% !important;
            object-fit: cover;
            object-position: center;
            display: block;
            border-radius: 8px;
        }
    }

    .img.preview-media {
        height: 100px !important;
        width: 100px !important;
        min-width: 100px !important;
        max-width: 100px !important;
    }

    .rotate-icon {
        width: 22px !important;
        height: 22px !important;
        min-width: 22px !important;
        min-height: 22px !important;
        max-width: 22px !important;
        max-height: 22px !important;
        padding: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        line-height: 0 !important;
        overflow: visible;
    }

    .rotate-icon svg,
    .rotate-icon i.icon {
        display: block !important;
        width: 14px !important;
        height: 14px !important;
        margin: 0 !important;
        flex: 0 0 14px !important;
    }
`

const claimMediaRotationKey = mediaItem =>
    getMediaMergeKey(mediaItem) ||
    String(mediaItem?.id || mediaItem?.url || mediaItem?.FileKey || '')

/** View-only 90° turn. Does not replace or save the stored image. */
const withClaimViewRotation = (files, rotationByKey) =>
    (files || []).map(file => {
        const viewRotation = rotationByKey[claimMediaRotationKey(file)] || 0
        return viewRotation ? { ...file, viewRotation } : file
    })

const nextClaimViewRotation = (rotationByKey, mediaItem) => {
    const key = claimMediaRotationKey(mediaItem)
    if (!key) return rotationByKey
    return {
        ...rotationByKey,
        [key]: ((rotationByKey[key] || 0) + 90) % 360,
    }
}

/** Same preview/upload flow as normal inspection `MediaTypeFilesBox`. */
const ClaimMediaFileUpload = ({
    files = [],
    onAddFiles,
    onRemoveFile,
    onSaveComment,
    disabled,
    canRemove,
}) => {
    const [previewFiles, setPreviewFiles] = useState([])
    const [rotationByKey, setRotationByKey] = useState({})
    const [isMediaUploading, setIsMediaUploading] = useState(false)
    const canUpload = !disabled && typeof onAddFiles === 'function'

    const mediaSyncKey = useMemo(
        () =>
            (files || [])
                .map(
                    file =>
                        `${getMediaMergeKey(file) || ''}:${file?.SignedURL ?? file?.FileURL ?? file?.url ?? ''}:${file?.Caption ?? ''}:${file?.Comment ?? ''}`,
                )
                .join('|'),
        [files],
    )

    useEffect(() => {
        setPreviewFiles(prev =>
            mergeLocalMediaState(
                mapInspectionQuestionMediaFiles({
                    Files: Array.isArray(files) ? files : [],
                }),
                prev,
            ),
        )
    }, [mediaSyncKey, files])

    const handleAddFiles = useCallback(
        async nextFiles => {
            if (!canUpload) return
            setIsMediaUploading(true)
            try {
                await onAddFiles(nextFiles)
            } catch (error) {
                console.error('Error uploading claim media:', error)
            } finally {
                setIsMediaUploading(false)
            }
        },
        [canUpload, onAddFiles],
    )

    const handleRemoveFile = useCallback(
        async file => {
            if (file?.url?.startsWith('blob:')) {
                try {
                    URL.revokeObjectURL(file.url)
                } catch (error) {
                    // ignore revoke errors
                }
            }
            if (typeof onRemoveFile === 'function') {
                const updated = await onRemoveFile(file)
                if (updated) {
                    setPreviewFiles(mapInspectionQuestionMediaFiles(updated))
                    return
                }
            }
            setPreviewFiles(prev =>
                prev.filter(
                    item =>
                        item.id !== file.id &&
                        item.MediaId !== file.MediaId,
                ),
            )
        },
        [onRemoveFile],
    )

    const canEditMedia = !disabled && canUpload

    const onRotateImage = mediaItem => {
        setRotationByKey(prev => nextClaimViewRotation(prev, mediaItem))
    }

    const visibleFiles = useMemo(
        () => withClaimViewRotation(previewFiles, rotationByKey),
        [previewFiles, rotationByKey],
    )

    return (
        <ClaimMediaUploadWrap className="claim-media-preview">
            <FileUploadPicker
                label=""
                customeLabel
                dense
                miniActions
                stackMediaButtons={false}
                mediaActionIcons
                maxFiles={5}
                maxFileSize={500}
                accept={AcceptFileType.all}
                files={visibleFiles}
                addedNewFiles={handleAddFiles}
                onRemove={handleRemoveFile}
                remove={canRemove && !!onRemoveFile && canEditMedia}
                isUploading={isMediaUploading}
                minHeight="100px"
                customePreview
                previewItem={
                    <CustomePreview
                        allowMetaEdit={canEditMedia}
                        onRotate={onRotateImage}
                        saveComment={
                            canEditMedia && typeof onSaveComment === 'function'
                                ? onSaveComment
                                : () => {}
                        }
                    />
                }
                previewItemClassName="preview-media"
                uploadButtonText="Upload Picture"
                captureButtonText="Take Picture"
                showTakePicture
                disabled={disabled || !canUpload}
            />
        </ClaimMediaUploadWrap>
    )
}

/** Finish Mat horizontal table media icons — compact upload/camera + preview eye. */
const ClaimInlineMediaStyles = createGlobalStyle`
    .claim-inline-media-icons {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        flex-wrap: nowrap;
        flex-shrink: 0;
        min-height: 28px;
    }
    .claim-inline-media-icons .horizontal-media-action-picker {
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;
    }
    .claim-inline-media-icons .horizontal-media-action-picker > .field,
    .claim-inline-media-icons .horizontal-media-action-picker .field.mb-0,
    .claim-inline-media-icons .horizontal-media-action-picker .field.col-12 {
        margin: 0 !important;
        padding: 0 !important;
        width: auto !important;
        max-width: none !important;
        flex: 0 0 auto !important;
    }
    .claim-inline-media-icons .horizontal-media-action-picker .field > .field {
        margin: 0 !important;
    }
    .claim-inline-media-icons .horizontal-media-action-picker .ui.buttons,
    .claim-inline-media-icons .horizontal-media-action-picker .ui.buttons .button,
    .claim-inline-media-icons .horizontal-media-action-picker .ui.button {
        margin: 0 !important;
    }
    .claim-inline-media-icons .horizontal-media-action-picker .ui.mini.button,
    .claim-inline-media-icons .horizontal-media-action-picker .ui.button.mini,
    .claim-inline-media-icons .horizontal-media-action-picker .ui.compact.mini.button {
        min-width: 28px !important;
        width: 28px !important;
        height: 28px !important;
        min-height: 28px !important;
        padding: 0 !important;
        border-radius: 8px !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        box-shadow: none !important;
        line-height: 1 !important;
    }
    .claim-inline-media-icons .horizontal-media-action-picker .ui.mini.button i.icon {
        margin: 0 !important;
        font-size: 12px !important;
        width: auto !important;
        height: auto !important;
        line-height: 1 !important;
    }
    .claim-inline-media-icons .horizontal-media-view-btn.ui.button {
        min-width: 28px !important;
        width: 28px !important;
        height: 28px !important;
        min-height: 28px !important;
        padding: 0 !important;
        border-radius: 8px !important;
        background: #eef2f7 !important;
        color: #3f4d63 !important;
        border: 1px solid #d8e0ea !important;
        box-shadow: none !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        line-height: 1 !important;
    }
    .claim-inline-media-icons .horizontal-media-view-btn.ui.button:hover {
        background: #e1e9f2 !important;
    }
    .ui.modal.claim-inline-media-modal .horizontal-media-preview-item {
        width: 110px;
        height: 110px;
        position: relative;
        flex-shrink: 0;
        overflow: visible;
    }
    .ui.modal.claim-inline-media-modal .comment-text-area-popup > div {
        width: 110px;
        height: 110px;
        display: block;
    }
    .ui.modal.claim-inline-media-modal .horizontal-media-preview-item .img.col-4,
    .ui.modal.claim-inline-media-modal
        .horizontal-media-preview-item
        .horizontal-modal-preview-thumb {
        width: 110px !important;
        min-width: 110px !important;
        max-width: 110px !important;
        height: 110px !important;
        flex: 0 0 110px !important;
        padding: 0 !important;
        margin: 0 !important;
        display: block !important;
        box-sizing: border-box;
        position: relative !important;
        overflow: hidden;
    }
    .ui.modal.claim-inline-media-modal .horizontal-media-preview-item .img img {
        width: 100% !important;
        height: 100% !important;
        object-fit: cover;
        border-radius: 6px;
    }
    .ui.modal.claim-inline-media-modal .horizontal-media-preview-item .delete-icon,
    .ui.modal.claim-inline-media-modal .horizontal-media-preview-item .rotate-icon {
        position: absolute !important;
        top: 5px !important;
        bottom: auto !important;
        width: 22px !important;
        height: 22px !important;
        min-width: 22px !important;
        min-height: 22px !important;
        max-width: 22px !important;
        max-height: 22px !important;
        padding: 0 !important;
        margin: 0 !important;
        flex: none !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        line-height: 0 !important;
        z-index: 5;
    }
    .ui.modal.claim-inline-media-modal .horizontal-media-preview-item .rotate-icon svg {
        display: block !important;
        width: 14px !important;
        height: 14px !important;
        margin: 0 !important;
        flex: 0 0 14px !important;
    }
    .ui.modal.claim-inline-media-modal .horizontal-media-preview-item .delete-icon {
        right: 5px !important;
        left: auto !important;
    }
    .ui.modal.claim-inline-media-modal .horizontal-media-preview-item .rotate-icon {
        left: 5px !important;
        right: auto !important;
    }
    .ui.modal.claim-inline-media-modal .horizontal-media-preview-item .rotate-icon i.icon {
        font-size: 12px !important;
        width: auto !important;
        height: auto !important;
        margin: 0 !important;
        line-height: 1 !important;
    }
`

const ClaimInlineMediaRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;

    .claim-inline-answer {
        flex: 1 1 auto;
        min-width: 0;

        .field {
            margin-bottom: 0 !important;
        }

        .ui.input,
        .ui.selection.dropdown,
        .react-datepicker-wrapper,
        .react-datepicker__input-container,
        .react-datepicker__input-container input {
            width: 100% !important;
        }
    }

    .claim-inline-media-slot {
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        align-self: center;
    }
`

const shouldShowClaimInlineMedia = (question, { skip = false } = {}) => {
    if (skip || !question?.Id) return false
    if (Number(question?.QuestionResponseId) === ControlType.Media) return false
    return question?.QuestionDisplaySetting?.IsMediaAllowed !== false
}

const ClaimInlineMediaActions = ({
    mediaQuestion,
    sectionId,
    addMedia,
    removeMedia,
    onSaveComment,
    disabled,
}) => {
    const [files, setFiles] = useState([])
    const [rotationByKey, setRotationByKey] = useState({})
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const [isMediaUploading, setIsMediaUploading] = useState(false)
    const canUploadMedia = !disabled && typeof addMedia === 'function'
    const hasFiles = Array.isArray(files) && files.length > 0

    const mediaSyncKey = useMemo(() => {
        const list = getQuestionMediaList(mediaQuestion)
        return list
            .map(
                m =>
                    `${m.MediaId ?? ''}:${m.FileKey ?? ''}:${m.SignedURL ?? m.FileURL ?? m.url ?? ''}:${m.Caption ?? ''}:${m.Comment ?? ''}`,
            )
            .join('|')
    }, [
        mediaQuestion?.Files,
        mediaQuestion?.Answers?.MediaResponse,
        mediaQuestion?.MediaAnswers,
    ])

    useEffect(() => {
        setFiles(prev =>
            mergeLocalMediaState(
                mapInspectionQuestionMediaFiles(mediaQuestion),
                prev,
            ),
        )
    }, [mediaSyncKey, mediaQuestion?.Id])

    const onFileUpload = async uploadFiles => {
        if (!canUploadMedia || !mediaQuestion?.Id) return
        setIsMediaUploading(true)
        try {
            await addMedia(mediaQuestion.Id, uploadFiles, sectionId)
        } catch (error) {
            console.error('Error uploading claim inline media:', error)
        } finally {
            setIsMediaUploading(false)
        }
    }

    const onRemoveFile = async file => {
        if (!canUploadMedia || !mediaQuestion?.Id) return
        if (file?.url?.startsWith('blob:')) {
            try {
                URL.revokeObjectURL(file.url)
            } catch (error) {
                // ignore
            }
        }
        if (typeof removeMedia === 'function') {
            const updated = await removeMedia(
                mediaQuestion.Id,
                file,
                sectionId,
            )
            setFiles(mapInspectionQuestionMediaFiles(updated || mediaQuestion))
        }
    }

    const onRotateImage = mediaItem => {
        setRotationByKey(prev => nextClaimViewRotation(prev, mediaItem))
    }

    const visibleFiles = useMemo(
        () => withClaimViewRotation(files, rotationByKey),
        [files, rotationByKey],
    )

    const saveComment = async (mediaItem, meta) => {
        if (!canUploadMedia || !mediaQuestion?.Id || typeof onSaveComment !== 'function') {
            return null
        }
        const hasChanged =
            (meta?.Comment ?? '') !== (mediaItem?.Comment ?? '') ||
            (meta?.Caption ?? '') !== (mediaItem?.Caption ?? '')
        if (!hasChanged) return null
        try {
            await onSaveComment(mediaItem, meta)
        } catch (error) {
            console.error('Error saving claim inline media comment:', error)
        }
        return null
    }

    const previewGridStyle = {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 110px)',
        gap: '12px',
        maxHeight: '52vh',
        overflow: 'auto',
        alignItems: 'start',
        justifyContent: 'start',
    }
    const previewItemStyle = {
        position: 'relative',
        width: 110,
        height: 110,
    }

    return (
        <>
            <div className="claim-inline-media-icons">
                <div className="horizontal-media-action-picker">
                    <FileUploadPicker
                        label=""
                        customeLabel
                        dense
                        miniActions
                        stackMediaButtons={false}
                        mediaActionIcons
                        maxFiles={5}
                        maxFileSize={500}
                        accept={AcceptFileType.all}
                        files={files}
                        addedNewFiles={onFileUpload}
                        onRemove={onRemoveFile}
                        isUploading={isMediaUploading}
                        noPreview
                        remove={false}
                        uploadButtonText=""
                        captureButtonText=""
                        showTakePicture
                        disabled={!canUploadMedia}
                    />
                </div>
                {hasFiles ? (
                    <Button
                        type="button"
                        size="mini"
                        icon
                        className="horizontal-media-view-btn"
                        title="View uploaded media"
                        onClick={() => setIsPreviewOpen(true)}
                    >
                        <Eye size={14} weight="bold" />
                    </Button>
                ) : null}
            </div>
            <Modal
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                className="claim-inline-media-modal"
                size="small"
                style={{ maxWidth: 520 }}
            >
                <Modal.Header>
                    <FlexRowBetween>
                        <span>Uploaded Media</span>
                        <X
                            className="pointer"
                            size={28}
                            onClick={() => setIsPreviewOpen(false)}
                        />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content scrolling>
                    {hasFiles ? (
                        <div
                            className="horizontal-media-preview-grid"
                            style={previewGridStyle}
                        >
                            {visibleFiles.map((file, index) => {
                                const key = String(
                                    file?.id ||
                                        file?.MediaId ||
                                        file?.url ||
                                        file?.SignedURL ||
                                        file?.FileURL ||
                                        index,
                                )
                                return (
                                    <div
                                        className="horizontal-media-preview-item"
                                        key={key}
                                        style={{
                                            ...previewItemStyle,
                                            overflow: 'visible',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 110,
                                                height: 110,
                                                display: 'block',
                                            }}
                                        >
                                            <CustomePreview
                                                item={file}
                                                allowMetaEdit={canUploadMedia}
                                                onRotate={onRotateImage}
                                                saveComment={
                                                    canUploadMedia
                                                        ? saveComment
                                                        : () => {}
                                                }
                                                remove={canUploadMedia}
                                                onRemove={
                                                    canUploadMedia
                                                        ? onRemoveFile
                                                        : undefined
                                                }
                                                className="horizontal-modal-preview-thumb"
                                            />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        <div>No media uploaded yet.</div>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        type="button"
                        onClick={() => setIsPreviewOpen(false)}
                    >
                        Close
                    </Button>
                </Modal.Actions>
            </Modal>
        </>
    )
}

/** Response field + Finish Mat-style inline media icons on the same row. */
const ClaimFieldWithInlineMedia = ({
    question,
    sectionId,
    addMedia,
    removeMedia,
    onSaveComment,
    mediaDisabled,
    skipInlineMedia = false,
    label,
    renderField,
}) => {
    const showInline = shouldShowClaimInlineMedia(question, {
        skip: skipInlineMedia,
    })

    if (!showInline) {
        return renderField({ label })
    }

    return (
        <Form.Field>
            {label ? <label>{label}</label> : null}
            <ClaimInlineMediaRow>
                <div className="claim-inline-answer">
                    {renderField({ label: null })}
                </div>
                <div className="claim-inline-media-slot">
                    <ClaimInlineMediaActions
                        mediaQuestion={question}
                        sectionId={sectionId}
                        addMedia={addMedia}
                        removeMedia={removeMedia}
                        onSaveComment={onSaveComment}
                        disabled={mediaDisabled}
                    />
                </div>
            </ClaimInlineMediaRow>
        </Form.Field>
    )
}

const SupplierDropdownFieldContainer = styled.div`
    position: relative;
    width: 100%;
`

const SupplierDropdownValueLayer = styled.div.attrs({
    className: 'supplier-dropdown-value-layer',
})`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 5;
    height: 2.71428571em;
    display: flex;
    align-items: center;
    padding: 0 4.25em 0 1em;
    pointer-events: none;
    line-height: 1.21428571em;
    overflow: hidden;
    box-sizing: border-box;
`

const SupplierDropdownWrap = styled.div`

    /* Closed: show label/placeholder only (like Mat Type / Size) */
    .ui.selection.dropdown:not(.active):not(.visible) > input.search {
        display: none !important;
    }

    /*
     * Single-select: custom overlay replaces .text — collapse hidden text so it
     * does not reserve extra height. Overlay is anchored to bottom of field container
     * (dropdown only), not the label.
     */
    .ui.selection.dropdown.supplier-show-custom-value:not(.active):not(.visible) {
        min-height: 2.71428571em !important;
        line-height: 1.21428571em !important;
        padding: 0.78571429em 2.1em 0.78571429em 1em !important;
    }

    .ui.selection.dropdown.supplier-show-custom-value:not(.active):not(.visible)
        > .text,
    .ui.search.selection.dropdown.supplier-show-custom-value:not(.active):not(.visible)
        > .text {
        display: none !important;
    }

    .ui.search.selection.dropdown.supplier-show-custom-value:not(.active):not(.visible)
        > .icon.input {
        display: none !important;
    }

    .ui.search.selection.dropdown.supplier-show-custom-value:not(.active):not(.visible) {
        min-height: 2.71428571em !important;
        line-height: 1.21428571em !important;
        padding: 0.78571429em 2.1em 0.78571429em 1em !important;
    }

    .ui.selection.dropdown.supplier-show-custom-value > .dropdown.icon,
    .ui.search.selection.dropdown.supplier-show-custom-value > .dropdown.icon {
        top: 50% !important;
        bottom: auto !important;
        margin-top: 0 !important;
        transform: translateY(-50%) !important;
    }

    /* Open for search: show search input, hide selected label */
    .ui.selection.dropdown.supplier-dropdown-open:not(.supplier-trigger-selection)
        > .text,
    .ui.selection.dropdown.supplier-dropdown-open:not(.supplier-trigger-selection)
        > .default.text {
        display: none !important;
    }

    .ui.selection.dropdown.supplier-dropdown-open:not(.supplier-trigger-selection)
        > input.search {
        display: block !important;
        width: 100% !important;
        left: 0 !important;
        right: auto !important;
        padding: 0.67857143em 2.1em 0.67857143em 1em !important;
        border: none !important;
        background: transparent !important;
        caret-color: #1a2b4b;
    }

    .ui.selection.dropdown.supplier-dropdown-open:not(.supplier-trigger-selection)
        > input.search::placeholder {
        color: rgba(0, 0, 0, 0.45);
    }

    /* Just selected while menu may still be closing: show selection in trigger */
    .ui.selection.dropdown.supplier-trigger-selection > input.search {
        display: none !important;
    }

    .ui.selection.dropdown.supplier-trigger-selection > .text {
        display: block !important;
        width: 100%;
        padding-top: 0.35rem;
        padding-bottom: 0.35rem;
    }

    .ui.selection.dropdown.supplier-trigger-selection > .default.text {
        display: none !important;
    }

    .ui.selection.dropdown > .default.text {
        color: rgba(0, 0, 0, 0.6);
    }

    /* Dropdown stays white; selected details use summary panel below */
    .ui.selection.dropdown.supplier-selected {
        background: #fff !important;
        border-color: rgba(34, 36, 38, 0.15) !important;
    }

    .ui.dropdown .menu > .item .supplier-option-row,
    .supplier-dropdown-value-layer .supplier-option-row {
        min-height: 1.21428571em;
    }

    .supplier-dropdown-value-layer .supplier-option-row {
        width: 100%;
    }

    .ui.selection.dropdown.supplier-selected > .default.text {
        display: none !important;
    }

    .ui.selection.dropdown > .remove.icon,
    .ui.selection.dropdown > .delete.icon,
    .ui.search.selection.dropdown > .remove.icon,
    .ui.search.selection.dropdown > .delete.icon,
    .ui.search.selection > .icon.input > .remove.icon {
        top: 50% !important;
        bottom: auto !important;
        margin: 0 !important;
        transform: translateY(-50%) !important;
        height: 1em !important;
        line-height: 1 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
    }

    .ui.search.selection > .icon.input:not([class*='left icon'])
        > .icon
        ~ .remove.icon {
        right: 1.85714em !important;
    }

    .ui.dropdown .menu {
        max-height: 320px !important;
    }

    .ui.dropdown .menu > .item.supplier-group-header {
        font-weight: 700 !important;
        color: #1a2b4b !important;
        background: #f8fafc !important;
        cursor: default !important;
        opacity: 1 !important;
        border-top: 1px solid #eef1f5;
    }

    .ui.dropdown .menu > .item.supplier-group-header:first-child {
        border-top: none;
    }

    .ui.dropdown .menu > .item:not(.supplier-group-header) {
        display: flex !important;
        align-items: center !important;
    }
`

const SUPPLIER_BADGE_COLORS = {
    cintas: '#00b5d8',
    international: '#7c3aed',
    guardian: '#10b981',
    our: '#10b981',
}

const customerTypeLabelColor = value => {
    if (value === 'C') return SUPPLIER_BADGE_COLORS.cintas
    if (value === 'I') return SUPPLIER_BADGE_COLORS.international
    if (value === 'L') return SUPPLIER_BADGE_COLORS.guardian
    return '#1a2b4b'
}

const CustomerTypeField = styled(Form.Field)`
    margin-bottom: 1rem !important;
`

const CustomerTypeLabel = styled.label`
    display: block;
    font-weight: 700;
    color: #1a2b4b;
    margin-bottom: 0.5rem !important;
`

const CustomerTypeRadioGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
`

const CustomerTypeRadioItem = styled.div`
    margin: 0 !important;

    .ui.radio.checkbox {
        margin: 0 !important;
    }
`

const CustomerTypeRadioLabel = styled.label`
    &&& {
        color: ${props => customerTypeLabelColor(props.$value)} !important;
        font-weight: 700 !important;
    }
`

const SupplierOptionRow = styled.div.attrs({
    className: 'supplier-option-row',
})`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    min-width: 0;
    line-height: 1.21428571em;
`

/** Location + city/state/zip flow inline (reference mockup), not pushed to far right */
const SupplierOptionTextGroup = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
`

const SupplierOptionMain = styled.span`
    flex: 0 1 auto;
    font-weight: 800;
    color: #1a2b4b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const SupplierOptionAddress = styled.span`
    flex: 0 1 auto;
    color: #8a96a8;
    font-size: 0.875em;
    font-weight: 400;
    line-height: inherit;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const SupplierGroupHeader = styled.div`
    font-weight: 700;
    font-size: 0.8rem;
    color: #1a2b4b;
    letter-spacing: 0.02em;
`

const SupplierOptionDisplay = ({ meta, summary = false }) => {
    if (!meta) return null
    const dropdownLocationLine =
        meta.cityStateZip ||
        formatSupplierCityStateZip({
            city: meta.city,
            stateProvince: meta.stateProvince,
            zip: meta.zip,
        })
    const summaryAddress =
        meta.formattedAddress || dropdownLocationLine || null

    if (summary) {
        return (
            <div>
                <SupplierOptionRow>
                    <SupplierOptionMain>{meta.locationName}</SupplierOptionMain>
                </SupplierOptionRow>
                {summaryAddress ? (
                    <SupplierSummaryAddress>{summaryAddress}</SupplierSummaryAddress>
                ) : null}
            </div>
        )
    }
    return (
        <SupplierOptionRow>
            <SupplierOptionTextGroup>
                <SupplierOptionMain>{meta.locationName}</SupplierOptionMain>
                {dropdownLocationLine ? (
                    <SupplierOptionAddress>
                        {dropdownLocationLine}
                    </SupplierOptionAddress>
                ) : null}
            </SupplierOptionTextGroup>
        </SupplierOptionRow>
    )
}

const SupplierSummaryAddress = styled.div`
    margin-top: 0.35rem;
    color: #6b7c93;
    font-size: 0.88rem;
    line-height: 1.4;
`

const ComplaintMediaBlock = styled.div`
    margin-bottom: 0.75rem;
`

const NotesMediaBlock = styled.div`
    margin-bottom: 0.75rem;
    overflow: visible;
`

/** Notes textarea with optional media upload (matches standard inspection NoteMediaActions). */
const ClaimNotesWithMediaField = ({
    question,
    questionId,
    control,
    name,
    label,
    placeholder = 'Enter text here',
    disabled,
    blockMediaUpload,
    mediaFiles = [],
    onMediaFiles,
    onRemoveMediaFiles,
    onSaveComment,
    minHeight = '120px',
    onChange,
}) => {
    const controlTypeId = Number(question?.QuestionResponseId)
    const isMediaOnly = controlTypeId === ControlType.Media
    const isMediaAllowed =
        question?.QuestionDisplaySetting?.IsMediaAllowed !== false
    const showMediaUpload =
        !!questionId && isMediaAllowed && !isMediaOnly
    const mediaBlocked = blockMediaUpload || disabled

    if (!question) {
        return (
            <ClaimInspectionTextAreaField
                control={control}
                name={name}
                label={label}
                placeholder={placeholder}
                minHeight={minHeight}
                disabled={disabled}
                onChange={onChange}
            />
        )
    }

    if (isMediaOnly) {
        return (
            <ClaimInspectionDynamicField
                question={question}
                control={control}
                name={name}
                label={label}
                placeholder={placeholder}
                disabled={disabled}
                mediaFiles={mediaFiles}
                onMediaFiles={onMediaFiles}
                onRemoveMediaFiles={onRemoveMediaFiles}
                onSaveComment={onSaveComment}
                mediaDisabled={mediaBlocked || !questionId}
                minHeight={minHeight}
                onChange={onChange}
            />
        )
    }

    return (
        <Form.Field>
            {label ? <label>{label}</label> : null}
            {showMediaUpload ? (
                <NotesMediaBlock>
                    <ClaimMediaFileUpload
                        files={mediaFiles}
                        onAddFiles={files => onMediaFiles?.(files)}
                        onRemoveFile={onRemoveMediaFiles}
                        onSaveComment={onSaveComment}
                        disabled={mediaBlocked || !questionId}
                        canRemove={
                            !mediaBlocked && !!onRemoveMediaFiles
                        }
                    />
                </NotesMediaBlock>
            ) : null}
            <ClaimInspectionDynamicField
                question={question}
                control={control}
                name={name}
                placeholder={placeholder}
                disabled={disabled}
                minHeight={minHeight}
                onChange={onChange}
            />
        </Form.Field>
    )
}

const SectionHeaderWithCopy = ({
    title,
    sectionId,
    onCopySection,
    readOnly,
    rightContent,
}) => (
    <CardHeader>
        <span>{title}</span>
        <HeaderActions>
            {rightContent}
            {sectionId != null && onCopySection ? (
                <CopyHeaderButton
                    type="button"
                    disabled={readOnly}
                    onClick={() => onCopySection(sectionId)}
                >
                    <Plus size={16} weight="bold" />
                    Add New
                </CopyHeaderButton>
            ) : null}
        </HeaderActions>
    </CardHeader>
)

const sanitizeClaimNumberInput = raw => {
    const rawValue = String(raw ?? '')
    const parts = rawValue.replace(/[^0-9.]/g, '').split('.')
    return parts.length > 1
        ? `${parts[0]}.${parts.slice(1).join('')}`
        : parts[0]
}

/** Number input — same as inspection `ControlType.Number` (stepper arrows). */
const ClaimInspectionNumberField = ({
    control,
    name,
    label,
    placeholder = 'Enter Numbers',
    disabled,
    onChange,
    error,
    min,
    step = 'any',
}) => (
    <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
            <Form.Field error={!!error?.message || !!fieldState.error}>
                {label ? <label>{label}</label> : null}
                <Input
                    fluid
                    className="score-input"
                    type="number"
                    inputMode="decimal"
                    placeholder={placeholder}
                    value={field.value ?? ''}
                    disabled={disabled}
                    error={!!error?.message}
                    min={min}
                    step={step}
                    onBlur={field.onBlur}
                    onKeyDown={event => {
                        if (['e', 'E', '+', '-'].includes(event.key)) {
                            event.preventDefault()
                        }
                    }}
                    onPaste={event => {
                        event.preventDefault()
                        const pastedText =
                            event.clipboardData?.getData('text') ?? ''
                        const sanitized =
                            sanitizeClaimNumberInput(pastedText)
                        field.onChange(sanitized)
                        onChange?.(sanitized)
                        if (event.target) {
                            event.target.value = sanitized
                        }
                    }}
                    onWheel={event => {
                        if (event?.target?.blur) {
                            event.target.blur()
                        }
                    }}
                    onChange={(_, data) => {
                        const sanitized = sanitizeClaimNumberInput(data.value)
                        field.onChange(sanitized)
                        onChange?.(sanitized)
                    }}
                />
            </Form.Field>
        )}
    />
)

/** Text input — same Semantic UI `Input` as standard inspection templates. */
const ClaimInspectionTextField = ({
    control,
    name,
    label,
    placeholder = 'Enter text here',
    disabled,
    onChange,
    error,
}) => (
    <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
            <Form.Field error={!!error?.message || !!fieldState.error}>
                {label ? <label>{label}</label> : null}
                <Input
                    fluid
                    type="text"
                    placeholder={placeholder}
                    value={field.value ?? ''}
                    disabled={disabled}
                    error={!!error?.message}
                    onBlur={field.onBlur}
                    onChange={(_, data) => {
                        field.onChange(data.value)
                        onChange?.(data.value)
                    }}
                />
            </Form.Field>
        )}
    />
)

const parseClaimDateValue = raw => {
    if (!raw) return null
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
        return raw
    }
    const parsed = new Date(raw)
    return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** Date / date-time — same `ReactDatePicker` as standard inspection (`DateTimeFormFieldWithoutControl`). */
const ClaimInspectionDateField = ({
    control,
    name,
    label,
    placeholder = 'Select date and time',
    disabled,
    onChange,
    showTimeInput = false,
}) => (
    <Controller
        control={control}
        name={name}
        render={({ field }) => (
            <Form.Field>
                {label ? <label>{label}</label> : null}
                <ReactDatePicker
                    showIcon
                    selected={parseClaimDateValue(field.value)}
                    onChange={date => {
                        field.onChange(date)
                        onChange?.(date)
                    }}
                    onBlur={field.onBlur}
                    placeholderText={placeholder}
                    disabled={disabled}
                    showTimeSelect={showTimeInput}
                    dateFormat={
                        showTimeInput ? 'MM/dd/yyyy h:mm aa' : 'MM/dd/yyyy'
                    }
                />
            </Form.Field>
        )}
    />
)

const mergeValueIntoDropdownOptions = (options, value) => {
    const list = Array.isArray(options) ? [...options] : []
    const extra = String(value ?? '').trim()
    if (!extra) return list
    if (list.some(opt => String(opt?.value ?? '').trim() === extra)) {
        return list
    }
    return [
        ...list,
        {
            key: `other-${extra}`,
            value: extra,
            text: extra,
        },
    ]
}

/** Dropdown that can add a typed "Other" value, same as normal inspection. */
const ClaimPortalDropdownField = ({
    control,
    name,
    label,
    options,
    disabled,
    question,
    onValueChange,
}) => {
    const [addedOptions, setAddedOptions] = useState([])
    const currentValue = useWatch({ control, name })
    const allowOther = questionAllowsOtherOption(question) && !disabled
    const mergedOptions = useMemo(
        () =>
            mergeValueIntoDropdownOptions(
                [...(options || []), ...addedOptions],
                currentValue,
            ),
        [options, addedOptions, currentValue],
    )

    return (
        <FormDropdownField
            control={control}
            name={name}
            label={label}
            placeholder="Select"
            options={mergedOptions}
            fluid
            clearable
            disabled={disabled}
            allowAdditions={allowOther}
            additionLabel="Add "
            additionPosition="top"
            selectOnNavigation={false}
            onAddItem={
                allowOther
                    ? (_, { value }) => {
                          const stringValue = String(value ?? '').trim()
                          if (!stringValue) return
                          setAddedOptions(prev => {
                              if (
                                  prev.some(
                                      opt =>
                                          String(opt?.value ?? '').trim() ===
                                          stringValue,
                                  )
                              ) {
                                  return prev
                              }
                              return [
                                  ...prev,
                                  {
                                      key: `other-${stringValue}`,
                                      value: stringValue,
                                      text: stringValue,
                                  },
                              ]
                          })
                          onValueChange?.(stringValue)
                      }
                    : undefined
            }
            onChange={(_, item) => onValueChange?.(item?.value || '')}
        />
    )
}

/**
 * Renders the answer control from the template question's `QuestionResponseId`
 * (same control types as standard inspection templates).
 */
const ClaimInspectionDynamicField = ({
    question,
    control,
    name,
    label,
    placeholder,
    disabled,
    onChange,
    dropdownOptions,
    mediaFiles,
    onMediaFiles,
    onRemoveMediaFiles,
    onSaveComment,
    mediaDisabled,
    minHeight = '120px',
}) => {
    if (!question) {
        return (
            <ClaimInspectionTextField
                control={control}
                name={name}
                label={label}
                placeholder={placeholder || 'Enter text here'}
                disabled={disabled}
                onChange={onChange}
            />
        )
    }

    const controlTypeId = Number(question?.QuestionResponseId)
    const isMultiple = isClaimMultipleChoiceQuestion(question)
    const options =
        dropdownOptions?.length > 0
            ? dropdownOptions
            : questionToDropdownOptions(question)

    if (isMultiple) {
        return (
            <ClaimPortalDropdownField
                control={control}
                name={name}
                label={label}
                options={options}
                disabled={disabled}
                question={question}
                onValueChange={val => onChange?.(val)}
            />
        )
    }

    switch (controlTypeId) {
        case ControlType.TextArea:
            return (
                <ClaimInspectionTextAreaField
                    control={control}
                    name={name}
                    label={label}
                    placeholder={placeholder || 'Enter text here'}
                    disabled={disabled}
                    minHeight={minHeight}
                    onChange={onChange}
                />
            )
        case ControlType.Number:
            return (
                <ClaimInspectionNumberField
                    control={control}
                    name={name}
                    label={label}
                    placeholder={placeholder || 'Enter Numbers'}
                    min={0}
                    step={1}
                    disabled={disabled}
                    onChange={onChange}
                />
            )
        case ControlType.Media:
            return (
                <Form.Field>
                    {label ? <label>{label}</label> : null}
                    <ClaimMediaFileUpload
                        files={mediaFiles}
                        onAddFiles={files => onMediaFiles?.(files)}
                        onRemoveFile={onRemoveMediaFiles}
                        onSaveComment={onSaveComment}
                        disabled={disabled || mediaDisabled}
                        canRemove={!disabled && !mediaDisabled}
                    />
                </Form.Field>
            )
        case ControlType.Checkbox:
            return (
                <Controller
                    control={control}
                    name={name}
                    render={({ field }) => (
                        <Form.Field>
                            {label ? <label>{label}</label> : null}
                            <Checkbox
                                toggle
                                checked={String(field.value) === 'true'}
                                disabled={disabled}
                                onChange={(_, data) => {
                                    const next = data.checked ? 'true' : 'false'
                                    field.onChange(next)
                                    onChange?.(next)
                                }}
                            />
                        </Form.Field>
                    )}
                />
            )
        case ControlType.DateAndTime:
            return (
                <ClaimInspectionDateField
                    control={control}
                    name={name}
                    label={label}
                    placeholder={placeholder || 'Select date and time'}
                    showTimeInput
                    disabled={disabled}
                    onChange={onChange}
                />
            )
        case ControlType.Date:
            return (
                <ClaimInspectionDateField
                    control={control}
                    name={name}
                    label={label}
                    placeholder={placeholder || 'Select date'}
                    disabled={disabled}
                    onChange={onChange}
                />
            )
        case ControlType.TextAnswer:
        default:
            return (
                <ClaimInspectionTextField
                    control={control}
                    name={name}
                    label={label}
                    placeholder={placeholder || 'Enter text here'}
                    disabled={disabled}
                    onChange={onChange}
                />
            )
    }
}

/** Text area — same Semantic UI `TextArea` as standard inspection templates. */
const ClaimInspectionTextAreaField = ({
    control,
    name,
    label,
    placeholder = 'Enter text here',
    disabled,
    onChange,
    minHeight = '100px',
}) => (
    <Controller
        control={control}
        name={name}
        render={({ field }) => (
            <Form.Field>
                {label ? <label>{label}</label> : null}
                <TextArea
                    placeholder={placeholder}
                    value={field.value ?? ''}
                    disabled={disabled}
                    style={{ minHeight }}
                    onBlur={field.onBlur}
                    onChange={(_, data) => {
                        field.onChange(data.value)
                        onChange?.(data.value)
                    }}
                />
            </Form.Field>
        )}
    />
)

const claimQuestionPlainText = question => {
    const raw = String(question?.FAQ || question?.Question || 'Question')
    return raw
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^\d+\.\s*/, '')
}

const serializeClaimExtraValue = (question, value) => {
    const type = Number(question?.QuestionResponseId)
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        if (type === ControlType.DateAndTime) return value.toISOString()
        const pad = n => String(n).padStart(2, '0')
        return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
    }
    if (value == null) return ''
    return value
}

const claimQuestionSpansFullWidth = question => {
    const type = Number(question?.QuestionResponseId)
    return (
        type === ControlType.TextArea ||
        type === ControlType.Media ||
        type === ControlType.Header ||
        type === ControlType.Label ||
        type === ControlType.Signature
    )
}

const ExtraQuestionGrid = styled(FieldGrid)`
    margin-top: ${props => (props.$flush ? '0' : '1rem')};
`

/**
 * One template question that is not part of the fixed claim field list.
 * Same two-column cell as Mat Type / Size, with the control type from the template.
 */
const ClaimExtraQuestionField = ({
    question,
    sectionId,
    label,
    value,
    onValueChange,
    disabled,
    mediaDisabled,
    templateData,
    addMedia,
    removeMedia,
    getClaimMediaSaveComment,
}) => {
    const { control, reset } = useForm({
        defaultValues: { value: value ?? '' },
    })
    const skipRef = useRef(value ?? '')

    useEffect(() => {
        const next = value ?? ''
        if (skipRef.current === next) return
        skipRef.current = next
        reset({ value: next })
    }, [value, reset])

    const controlTypeId = Number(question?.QuestionResponseId)
    if (
        controlTypeId === ControlType.Header ||
        controlTypeId === ControlType.Label
    ) {
        return (
            <div
                style={{
                    fontWeight: controlTypeId === ControlType.Header ? 700 : 600,
                    fontSize: controlTypeId === ControlType.Header ? 16 : 14,
                    color: '#1f2937',
                }}
            >
                {claimQuestionPlainText(question)}
            </div>
        )
    }

    const mediaFiles = mapInspectionQuestionMediaFiles(question)
    const saveComment = getClaimMediaSaveComment?.(question?.Id, sectionId)
    const options = questionToDropdownOptions(question, templateData)
    const handleChange = next => {
        const stored = serializeClaimExtraValue(question, next)
        skipRef.current = stored
        onValueChange?.(stored)
    }

    return (
        <ClaimFieldWithInlineMedia
            question={question}
            sectionId={sectionId}
            addMedia={addMedia}
            removeMedia={removeMedia}
            onSaveComment={saveComment}
            mediaDisabled={mediaDisabled || disabled}
            label={label}
            renderField={({ label: fieldLabel }) => (
                <ClaimInspectionDynamicField
                    question={question}
                    control={control}
                    name="value"
                    label={fieldLabel}
                    disabled={disabled}
                    dropdownOptions={options}
                    mediaFiles={mediaFiles}
                    onMediaFiles={
                        addMedia
                            ? files => addMedia(question?.Id, files, sectionId)
                            : undefined
                    }
                    onRemoveMediaFiles={
                        removeMedia
                            ? file => removeMedia(question?.Id, file, sectionId)
                            : undefined
                    }
                    onSaveComment={saveComment}
                    mediaDisabled={mediaDisabled || disabled}
                    onChange={handleChange}
                />
            )}
        />
    )
}

const ClaimTemplateQuestionGrid = ({
    questions,
    sectionId,
    startNumber = 1,
    flush = false,
    embedded = false,
    disabled,
    mediaDisabled,
    templateData,
    extraAnswers,
    onExtraAnswerChange,
    addMedia,
    removeMedia,
    getClaimMediaSaveComment,
}) => {
    if (!questions?.length) return null

    let number = startNumber
    const cells = questions.map(question => {
                const fullWidth = claimQuestionSpansFullWidth(question)
                const displayOnly =
                    Number(question?.QuestionResponseId) === ControlType.Header ||
                    Number(question?.QuestionResponseId) === ControlType.Label
                const label = displayOnly
                    ? claimQuestionPlainText(question)
                    : `${number}. ${claimQuestionPlainText(question)}`
                if (!displayOnly) number += 1
                const answerKey = claimExtraAnswerKey(sectionId, question)
                const value = Object.prototype.hasOwnProperty.call(
                    extraAnswers || {},
                    answerKey,
                )
                    ? extraAnswers[answerKey]
                    : readQuestionDisplayValue(question)
                const Cell = fullWidth ? FullWidth : GridFieldCell
                return (
                    <Cell key={answerKey}>
                        <ClaimExtraQuestionField
                            question={question}
                            sectionId={sectionId}
                            label={label}
                            value={value}
                            disabled={disabled}
                            mediaDisabled={mediaDisabled}
                            templateData={templateData}
                            addMedia={addMedia}
                            removeMedia={removeMedia}
                            getClaimMediaSaveComment={getClaimMediaSaveComment}
                            onValueChange={next =>
                                onExtraAnswerChange?.(answerKey, next)
                            }
                        />
                    </Cell>
                )
            })

    if (embedded) {
        return cells
    }

    return <ExtraQuestionGrid $flush={flush}>{cells}</ExtraQuestionGrid>
}

const GenericClaimSectionCard = ({
    section,
    readOnly,
    templatePreview,
    sectionWorkflowEnabled,
    sectionWorkflowStatuses,
    sectionWorkflowBusy,
    openSectionCompleteModal,
    openSectionUncompleteModal,
    templateData,
    extraAnswers,
    onExtraAnswerChange,
    addMedia,
    removeMedia,
    getClaimMediaSaveComment,
    onCopySection,
}) => {
    const workflowStatus = useMemo(() => {
        if (!sectionWorkflowEnabled) return null
        return resolveSectionWorkflowStatus(
            sectionWorkflowStatuses,
            section?.SectionId,
            null,
        )
    }, [sectionWorkflowEnabled, sectionWorkflowStatuses, section?.SectionId])

    const fieldsDisabled =
        readOnly ||
        (sectionWorkflowEnabled && isSectionWorkflowFieldsDisabled(workflowStatus))
    const mediaDisabled = readOnly || templatePreview || fieldsDisabled
    const questions = useMemo(
        () => listUnmatchedSectionQuestions(section, []),
        [section],
    )

    return (
        <Card>
            <SectionHeaderWithCopy
                title={section?.SectionName || 'Section'}
                sectionId={section?.SectionId}
                onCopySection={onCopySection}
                readOnly={fieldsDisabled}
                rightContent={
                    sectionWorkflowEnabled && workflowStatus ? (
                        <ClaimSectionStatusBadge status={workflowStatus} />
                    ) : null
                }
            />
            <CardBody>
                <ClaimSectionWorkflowBody status={workflowStatus}>
                    <ClaimInspectionForm>
                        <ClaimTemplateQuestionGrid
                            questions={questions}
                            sectionId={section?.SectionId}
                            startNumber={1}
                            flush
                            disabled={fieldsDisabled}
                            mediaDisabled={mediaDisabled}
                            templateData={templateData}
                            extraAnswers={extraAnswers}
                            onExtraAnswerChange={onExtraAnswerChange}
                            addMedia={addMedia}
                            removeMedia={removeMedia}
                            getClaimMediaSaveComment={getClaimMediaSaveComment}
                        />
                    </ClaimInspectionForm>
                </ClaimSectionWorkflowBody>
            </CardBody>
            {sectionWorkflowEnabled && workflowStatus ? (
                <ClaimSectionWorkflowFooter
                    status={workflowStatus}
                    allStatuses={sectionWorkflowStatuses}
                    completing={sectionWorkflowBusy}
                    onComplete={openSectionCompleteModal}
                    onUncomplete={openSectionUncompleteModal}
                />
            ) : null}
        </Card>
    )
}

const EMPTY_PORTAL = {
    matType: '',
    itemSketchNumber: '',
    size: '',
    quantity: '',
    sfoOrder: '',
    complaintDetails: '',
    code: '',
    rma: '',
    creditIssue: '',
    notes: '',
}

export const CustomerClaimPortalInspection = ({
    supplierOptions = [],
    matTypeOptions = [],
    sizeOptions = [],
    codeOptions = [],
    carrierOptions = [],
    packingOptions = [],
    dispositionOptions = [],
    statusOptions = [],
    creditIssuePortalOptions = [],
    creditIssueCompletionOptions = [],
    selectedSupplier = '',
    onSupplierChange,
    portalForm = EMPTY_PORTAL,
    onPortalFormChange,
    qualityForm = {},
    onQualityChange,
    completionForm = {},
    onCompletionChange,
    loggedRows = [],
    fieldErrors = {},
    showValidation = false,
    readOnly = false,
    /** Template preview: fields editable, no submit/save, no media upload. */
    templatePreview = false,
    portalFieldQuestions = {},
    itemSketchQuestionId,
    complaintDetailsQuestionId,
    complaintMediaFiles = [],
    portalNotesMediaFiles = [],
    qualityNotesMediaFiles = [],
    completionNotesMediaFiles = [],
    onComplaintMedia,
    onPortalNotesMedia,
    onQualityNotesMedia,
    onCompletionNotesMedia,
    onRemoveComplaintMedia,
    onRemovePortalNotesMedia,
    onRemoveQualityNotesMedia,
    onRemoveCompletionNotesMedia,
    onComplaintMediaSaveComment,
    onPortalNotesMediaSaveComment,
    onQualityNotesMediaSaveComment,
    onCompletionNotesMediaSaveComment,
    portalNotesQuestionId,
    qualityNotesQuestion,
    completionNotesQuestion,
    onCopySection,
    portalSectionId,
    qualitySectionId,
    completionSectionId,
    receivingDateUsesTime = false,
    sectionWorkflowEnabled = false,
    sectionWorkflowStatuses = [],
    claimNumber = '',
    claimCustomerName = '',
    onSectionComplete,
    onSectionUncomplete,
    sectionWorkflowBusy = false,
    claimPortalQuestionMap = {},
    claimQualityQuestionMap = {},
    claimCompletionQuestionMap = {},
    addMedia,
    removeMedia,
    getClaimMediaSaveComment,
    auditSections = [],
    templateData = null,
    onExtraAnswersChange,
    answersResetKey,
}) => {
    const loggedCount = loggedRows.length
    const displayClaimNumber = String(claimNumber || '').trim()
    const portalWorkflowStatus = useMemo(() => {
        if (!sectionWorkflowEnabled) return null
        return resolveSectionWorkflowStatus(
            sectionWorkflowStatuses,
            portalSectionId,
            1,
        )
    }, [sectionWorkflowEnabled, sectionWorkflowStatuses, portalSectionId])
    const qualityWorkflowStatus = useMemo(() => {
        if (!sectionWorkflowEnabled) return null
        return resolveSectionWorkflowStatus(
            sectionWorkflowStatuses,
            qualitySectionId,
            2,
        )
    }, [sectionWorkflowEnabled, sectionWorkflowStatuses, qualitySectionId])
    const completionWorkflowStatus = useMemo(() => {
        if (!sectionWorkflowEnabled) return null
        return resolveSectionWorkflowStatus(
            sectionWorkflowStatuses,
            completionSectionId,
            3,
        )
    }, [sectionWorkflowEnabled, sectionWorkflowStatuses, completionSectionId])
    const {
        modal: sectionWorkflowModal,
        openComplete: openSectionCompleteModal,
        openUncomplete: openSectionUncompleteModal,
    } = useClaimSectionWorkflowModal({
        onConfirmComplete: onSectionComplete,
        onConfirmUncomplete: onSectionUncomplete,
    })
    const [selectedCustomerType, setSelectedCustomerType] = useState('C')
    const [supplierSearchQuery, setSupplierSearchQuery] = useState('')
    const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false)
    const [supplierJustSelected, setSupplierJustSelected] = useState(false)
    const [addedSupplierOptions, setAddedSupplierOptions] = useState([])
    const supplierDropdownRef = useRef(null)

    const {
        itemSketch: itemSketchQuestion,
        quantity: quantityQuestion,
        sfoOrder: sfoOrderQuestion,
        rma: rmaQuestion,
        complaintDetails: complaintDetailsQuestion,
        notes: portalNotesQuestion,
    } = portalFieldQuestions

    const customerQuestion = useMemo(
        () => findQuestionByAliases(claimPortalQuestionMap, PORTAL_FAQ.customer),
        [claimPortalQuestionMap],
    )
    const matTypeQuestion = useMemo(
        () => findQuestionByAliases(claimPortalQuestionMap, PORTAL_FAQ.matType),
        [claimPortalQuestionMap],
    )
    const sizeQuestion = useMemo(
        () => findQuestionByAliases(claimPortalQuestionMap, PORTAL_FAQ.size),
        [claimPortalQuestionMap],
    )
    const codeQuestion = useMemo(
        () => findQuestionByAliases(claimPortalQuestionMap, PORTAL_FAQ.code),
        [claimPortalQuestionMap],
    )
    const portalCreditIssueQuestion = useMemo(
        () =>
            findQuestionByAliases(claimPortalQuestionMap, PORTAL_FAQ.creditIssue),
        [claimPortalQuestionMap],
    )

    /**
     * Customer is rendered above as Select Customer, so this section
     * numbers from 1 no matter what the section is named.
     * Non RMA has no RMA# — Credit Issue / Notes shift down by one.
     */
    const portalFieldLabels = useMemo(() => {
        const hasRma = Boolean(rmaQuestion)
        const label = (num, text) => `${num}. ${text}`
        return {
            matType: label(1, 'Mat Type'),
            size: label(2, 'Size'),
            itemSketch: label(3, 'Item/Sketch Number'),
            quantity: label(4, 'Quantity'),
            sfoOrder: label(5, 'SFO/Order #'),
            complaintDetails: label(6, 'Customer Complaint Details'),
            code: label(7, 'Code'),
            rma: hasRma ? label(8, 'RMA#') : null,
            creditIssue: label(hasRma ? 9 : 8, 'Credit Issue'),
            notes: label(hasRma ? 10 : 9, 'Notes'),
        }
    }, [rmaQuestion])
    const receivingDateQuestion = useMemo(
        () =>
            findQuestionByAliases(
                claimQualityQuestionMap,
                QUALITY_FAQ.receivingDate,
            ),
        [claimQualityQuestionMap],
    )
    const carrierQuestion = useMemo(
        () => findQuestionByAliases(claimQualityQuestionMap, QUALITY_FAQ.carrier),
        [claimQualityQuestionMap],
    )
    const embossQuestion = useMemo(
        () => findQuestionByAliases(claimQualityQuestionMap, QUALITY_FAQ.emboss),
        [claimQualityQuestionMap],
    )
    const packingQuestion = useMemo(
        () =>
            findQuestionByAliases(
                claimQualityQuestionMap,
                QUALITY_FAQ.packingMatCondition,
            ),
        [claimQualityQuestionMap],
    )
    const dispositionQuestion = useMemo(
        () =>
            findQuestionByAliases(claimQualityQuestionMap, QUALITY_FAQ.disposition),
        [claimQualityQuestionMap],
    )
    const trackingQuestion = useMemo(
        () =>
            findQuestionByAliases(claimQualityQuestionMap, QUALITY_FAQ.tracking),
        [claimQualityQuestionMap],
    )
    const statusQuestion = useMemo(
        () => findQuestionByAliases(claimQualityQuestionMap, QUALITY_FAQ.status),
        [claimQualityQuestionMap],
    )
    const completionCreditIssueQuestion = useMemo(
        () =>
            findQuestionByAliases(
                claimCompletionQuestionMap,
                COMPLETION_FAQ.creditIssue,
            ),
        [claimCompletionQuestionMap],
    )

    const [extraAnswers, setExtraAnswers] = useState({})
    const onExtraAnswersChangeRef = useRef(onExtraAnswersChange)
    onExtraAnswersChangeRef.current = onExtraAnswersChange

    useEffect(() => {
        setExtraAnswers({})
        onExtraAnswersChangeRef.current?.({})
    }, [answersResetKey])

    const updateExtraAnswer = useCallback((key, value) => {
        setExtraAnswers(prev => {
            if (prev[key] === value) return prev
            const next = { ...prev, [key]: value }
            onExtraAnswersChangeRef.current?.(next)
            return next
        })
    }, [])

    const findAuditSection = useCallback(
        sectionId =>
            (auditSections || []).find(
                section => String(section?.SectionId) === String(sectionId),
            ) || null,
        [auditSections],
    )

    const portalExtraQuestions = useMemo(
        () =>
            listUnmatchedSectionQuestions(findAuditSection(portalSectionId), [
                customerQuestion,
                matTypeQuestion,
                sizeQuestion,
                itemSketchQuestion,
                quantityQuestion,
                sfoOrderQuestion,
                complaintDetailsQuestion,
                codeQuestion,
                rmaQuestion,
                portalCreditIssueQuestion,
                portalNotesQuestion,
            ]),
        [
            findAuditSection,
            portalSectionId,
            customerQuestion,
            matTypeQuestion,
            sizeQuestion,
            itemSketchQuestion,
            quantityQuestion,
            sfoOrderQuestion,
            complaintDetailsQuestion,
            codeQuestion,
            rmaQuestion,
            portalCreditIssueQuestion,
            portalNotesQuestion,
        ],
    )

    const qualityExtraQuestions = useMemo(
        () =>
            listUnmatchedSectionQuestions(findAuditSection(qualitySectionId), [
                receivingDateQuestion,
                carrierQuestion,
                embossQuestion,
                packingQuestion,
                dispositionQuestion,
                trackingQuestion,
                statusQuestion,
                qualityNotesQuestion,
            ]),
        [
            findAuditSection,
            qualitySectionId,
            receivingDateQuestion,
            carrierQuestion,
            embossQuestion,
            packingQuestion,
            dispositionQuestion,
            trackingQuestion,
            statusQuestion,
            qualityNotesQuestion,
        ],
    )

    const completionExtraQuestions = useMemo(
        () =>
            listUnmatchedSectionQuestions(
                findAuditSection(completionSectionId),
                [completionCreditIssueQuestion, completionNotesQuestion],
            ),
        [
            findAuditSection,
            completionSectionId,
            completionCreditIssueQuestion,
            completionNotesQuestion,
        ],
    )

    const genericClaimSections = useMemo(() => {
        const used = new Set(
            [portalSectionId, qualitySectionId, completionSectionId]
                .filter(sectionId => sectionId != null && String(sectionId) !== '')
                .map(sectionId => String(sectionId)),
        )
        return sortAuditSectionsForClaim(auditSections).filter(section => {
            const sectionId = String(section?.SectionId ?? '')
            if (sectionId && used.has(sectionId)) return false
            if (isClaimPortalSection(section)) return false
            const name = String(section?.SectionName || '').trim()
            const questionCount = section?.Questions?.length || 0
            return Boolean(name) || questionCount > 0
        })
    }, [
        auditSections,
        portalSectionId,
        qualitySectionId,
        completionSectionId,
    ])

    const portalExtraStartNumber = (rmaQuestion ? 10 : 9) + 1
    const qualityExtraStartNumber = (statusQuestion ? 8 : 7) + 1
    // Both branches are 3. The length read keeps this list tied to the
    // completion grid for the linter on this large file.
    const completionExtraStartNumber = completionExtraQuestions.length > 0 ? 3 : 3

    const complaintDetailsControlType = Number(
        complaintDetailsQuestion?.QuestionResponseId,
    )
    const showComplaintDetailsMedia =
        !!complaintDetailsQuestionId &&
        complaintDetailsControlType !== ControlType.Media

    /** Portal fields stay editable without supplier (client mockup). Supplier required on submit only. */
    const isWorkflowSectionDisabled = status =>
        readOnly ||
        (sectionWorkflowEnabled && isSectionWorkflowFieldsDisabled(status))

    const customerFieldsDisabled =
        readOnly ||
        (sectionWorkflowEnabled &&
            isSectionWorkflowFieldsDisabled(portalWorkflowStatus))

    const portalFieldsDisabled = isWorkflowSectionDisabled(portalWorkflowStatus)
    const qualityFieldsDisabled = isWorkflowSectionDisabled(qualityWorkflowStatus)
    const completionFieldsDisabled = isWorkflowSectionDisabled(
        completionWorkflowStatus,
    )

    const portalMediaDisabled = readOnly || templatePreview || portalFieldsDisabled
    const qualityMediaDisabled =
        readOnly || templatePreview || qualityFieldsDisabled
    const completionMediaDisabled =
        readOnly || templatePreview || completionFieldsDisabled

    const {
        control: portalControl,
        reset: resetPortal,
        setValue: setPortalValue,
    } = useForm({ defaultValues: EMPTY_PORTAL })

    const {
        control: qualityControl,
        reset: resetQuality,
        setValue: setQualityValue,
    } = useForm({
        defaultValues: {
            receivingDate: null,
            carrier: '',
            tracking: '',
            emboss: '',
            packingMatCondition: '',
            disposition: '',
            status: '',
            notes: '',
        },
    })

    const {
        control: completionControl,
        reset: resetCompletion,
        setValue: setCompletionValue,
    } = useForm({ defaultValues: { creditIssue: '', notes: '' } })

    const { control: supplierControl, reset: resetSupplier, watch: watchSupplier } =
        useForm({
            defaultValues: { supplier: '' },
        })

    const supplierFieldValue = watchSupplier('supplier')
    const portalFormSnapshotRef = useRef('')

    useEffect(() => {
        const next = portalForm || EMPTY_PORTAL
        let json = ''
        try {
            json = JSON.stringify(next)
        } catch {
            json = ''
        }
        if (json && json === portalFormSnapshotRef.current) {
            return
        }
        portalFormSnapshotRef.current = json
        resetPortal(next)
    }, [portalForm, resetPortal])

    useEffect(() => {
        resetQuality(qualityForm || {})
    }, [qualityForm, resetQuality])

    useEffect(() => {
        resetCompletion(completionForm || { creditIssue: '', notes: '' })
    }, [completionForm, resetCompletion])

    useEffect(() => {
        resetSupplier({ supplier: selectedSupplier || '' })
    }, [selectedSupplier, resetSupplier])

    const hasCustomerTypeMeta = useMemo(
        () => supplierOptionsHaveClientType(supplierOptions),
        [supplierOptions],
    )

    const filteredSupplierOptions = useMemo(
        () =>
            filterSupplierOptionsByClientType(
                supplierOptions,
                hasCustomerTypeMeta ? selectedCustomerType : '',
            ),
        [supplierOptions, selectedCustomerType, hasCustomerTypeMeta],
    )

    useEffect(() => {
        if (!supplierFieldValue && !selectedSupplier) return
        const current = findSupplierOptionByValue(
            supplierOptions,
            supplierFieldValue || selectedSupplier,
        )
        if (!current?.meta) return
        const rowType = getClientTypeFromOptionMeta(current.meta)
        if (rowType && rowType !== selectedCustomerType) {
            onSupplierChange?.('')
            resetSupplier({ supplier: '' })
        }
    }, [
        selectedCustomerType,
        supplierFieldValue,
        selectedSupplier,
        supplierOptions,
        onSupplierChange,
        resetSupplier,
    ])

    const handleCustomerTypeChange = value => {
        setSelectedCustomerType(value)
        setSupplierSearchQuery('')
        setSupplierDropdownOpen(false)
        setSupplierJustSelected(false)
        resetSupplier({ supplier: '' })
        onSupplierChange?.('')
    }

    const updatePortal = (key, value) => {
        if (portalFieldsDisabled) return
        setPortalValue(key, value)
        if (onPortalFormChange) {
            onPortalFormChange(prev => ({ ...(prev || EMPTY_PORTAL), [key]: value }))
        }
    }

    const updateQuality = (key, value) => {
        if (qualityFieldsDisabled) return
        setQualityValue(key, value)
        if (onQualityChange) {
            onQualityChange(prev => ({ ...(prev || {}), [key]: value }))
        }
    }

    const updateCompletion = (key, value) => {
        if (completionFieldsDisabled) return
        setCompletionValue(key, value)
        if (onCompletionChange) {
            onCompletionChange(prev => ({
                ...(prev || { creditIssue: '', notes: '' }),
                [key]: value,
            }))
        }
    }

    const supplierErr = fieldErrors.__customer_claim_supplier__
    const allowSupplierOther =
        questionAllowsOtherOption(customerQuestion) && !customerFieldsDisabled

    const supplierDropdownOptions = useMemo(
        () => {
            const mapped = (filteredSupplierOptions || []).map(opt => {
                if (opt.disabled || opt.className === 'supplier-group-header') {
                    return {
                        ...opt,
                        content: (
                            <SupplierGroupHeader>{opt.text}</SupplierGroupHeader>
                        ),
                    }
                }
                if (opt.meta) {
                    return {
                        ...opt,
                        content: <SupplierOptionDisplay meta={opt.meta} />,
                    }
                }
                const text = opt.text || opt.value || ''
                return {
                    ...opt,
                    text,
                    content: <span>{text}</span>,
                }
            })
            const seen = new Set(
                mapped.map(opt => String(opt?.value ?? '').trim()).filter(Boolean),
            )
            const extras = []
            addedSupplierOptions.forEach(opt => {
                const value = String(opt?.value ?? '').trim()
                if (!value || seen.has(value)) return
                seen.add(value)
                extras.push({
                    ...opt,
                    text: opt.text || value,
                    content: opt.content || <span>{opt.text || value}</span>,
                })
            })
            const current = String(
                supplierFieldValue || selectedSupplier || '',
            ).trim()
            if (
                current &&
                !seen.has(current) &&
                !current.startsWith('__header_')
            ) {
                extras.push({
                    key: `other-${current}`,
                    value: current,
                    text: current,
                    content: <span>{current}</span>,
                })
            }
            return [...mapped, ...extras]
        },
        [
            filteredSupplierOptions,
            addedSupplierOptions,
            supplierFieldValue,
            selectedSupplier,
        ],
    )

    const selectedSupplierOption = useMemo(
        () =>
            findSupplierOptionByValue(
                filteredSupplierOptions,
                supplierFieldValue || selectedSupplier,
            ),
        [filteredSupplierOptions, supplierFieldValue, selectedSupplier],
    )

    const renderSupplierLabel = (item, _index, defaultLabel) => {
        const resolved =
            item?.meta != null
                ? item
                : selectedSupplierOption ||
                      findSupplierOptionByValue(filteredSupplierOptions, item?.value)
        if (!resolved?.value || String(resolved.value).startsWith('__header_')) {
            return defaultLabel
        }
        if (resolved.meta) {
            return <SupplierOptionDisplay meta={resolved.meta} />
        }
        return resolved.content || resolved.text || defaultLabel
    }

    const showSupplierCustomValue =
        !!selectedSupplierOption?.meta &&
        !!supplierFieldValue &&
        !supplierDropdownOpen &&
        !supplierSearchQuery

    const supplierDropdownClassName = [
        supplierFieldValue && !supplierDropdownOpen && !supplierSearchQuery
            ? 'supplier-selected'
            : '',
        showSupplierCustomValue ? 'supplier-show-custom-value' : '',
        supplierDropdownOpen ? 'supplier-dropdown-open' : '',
        supplierJustSelected && supplierFieldValue ? 'supplier-trigger-selection' : '',
    ]
        .filter(Boolean)
        .join(' ')

    const handleSupplierDropdownChange = (_, item) => {
        if (String(item?.value || '').startsWith('__header_')) {
            return
        }
        const value = item?.value ?? ''
        flushSync(() => {
            setSupplierSearchQuery('')
            setSupplierJustSelected(true)
            setSupplierDropdownOpen(false)
        })
        onSupplierChange?.(value)
        const node = supplierDropdownRef.current
        if (node?.blur) {
            node.blur()
        }
        if (node?.close) {
            node.close()
        }
        window.setTimeout(() => setSupplierJustSelected(false), 350)
    }

    const focusSupplierSearchInput = () => {
        window.setTimeout(() => {
            const candidates = [
                '.supplier-dropdown-open input.search',
                '.supplier-dropdown-open .search input',
                '.supplier-dropdown-open input[type="text"]',
                '.visible.active.selection.dropdown input.search',
            ]

            for (let i = 0; i < candidates.length; i += 1) {
                const searchInput = document.querySelector(candidates[i])
                if (searchInput && typeof searchInput.focus === 'function') {
                    searchInput.focus()
                    if (typeof searchInput.select === 'function') {
                        searchInput.select()
                    }
                    break
                }
            }
        }, 0)
    }

    return (
        <PageWrap>
            <ClaimInlineMediaStyles />
            <TopHeader>
                <HeaderTitle>
                    Customer Complaint Log
                    {displayClaimNumber ? (
                        <HeaderClaimNumber>{displayClaimNumber}</HeaderClaimNumber>
                    ) : null}
                </HeaderTitle>
                <HeaderMeta>
                    {loggedCount} complaint{loggedCount === 1 ? '' : 's'} logged
                </HeaderMeta>
            </TopHeader>

            <Content>
                {sectionWorkflowEnabled ? (
                    <ClaimNumberBanner
                        claimNumber={claimNumber}
                        customerName={claimCustomerName || selectedSupplier}
                    />
                ) : null}
                <Card>
                    <CardHeader>Select Customer</CardHeader>
                    <CardBody>
                        <ClaimInspectionForm>
                            <CustomerTypeField>
                                <CustomerTypeLabel>Customer Type</CustomerTypeLabel>
                                <CustomerTypeRadioGroup>
                                    {CUSTOMER_TYPE_RADIO_OPTIONS.map(option => (
                                        <CustomerTypeRadioItem key={option.value}>
                                            <Radio
                                                label={
                                                    <CustomerTypeRadioLabel
                                                        $value={option.value}
                                                    >
                                                        {option.label}
                                                    </CustomerTypeRadioLabel>
                                                }
                                                name="customerType"
                                                value={option.value}
                                                checked={
                                                    selectedCustomerType ===
                                                    option.value
                                                }
                                                disabled={customerFieldsDisabled}
                                                onChange={() =>
                                                    handleCustomerTypeChange(
                                                        option.value,
                                                    )
                                                }
                                            />
                                        </CustomerTypeRadioItem>
                                    ))}
                                </CustomerTypeRadioGroup>
                            </CustomerTypeField>
                            <SupplierDropdownWrap>
                                <SupplierDropdownFieldContainer>
                                    <FormDropdownField
                                    key={`supplier-${selectedCustomerType}`}
                                    control={supplierControl}
                                    name="supplier"
                                    label="Customer"
                                    placeholder="Select a customer"
                                    options={supplierDropdownOptions}
                                    clearable
                                    search={filterSupplierDropdownOptions}
                                    searchQuery={supplierSearchQuery}
                                    onSearchChange={(_, data) => {
                                        setSupplierSearchQuery(
                                            data.searchQuery ?? '',
                                        )
                                        setSupplierJustSelected(false)
                                    }}
                                    ref={supplierDropdownRef}
                                    onOpen={() => {
                                        setSupplierDropdownOpen(true)
                                        setSupplierSearchQuery('')
                                        setSupplierJustSelected(false)
                                        focusSupplierSearchInput()
                                    }}
                                    onClose={() => {
                                        setSupplierDropdownOpen(false)
                                        setSupplierSearchQuery('')
                                        setSupplierJustSelected(false)
                                    }}
                                    searchInput={{
                                        placeholder:
                                            'Type to search by location name...',
                                        autoFocus: true,
                                    }}
                                    fluid
                                    disabled={customerFieldsDisabled}
                                    className={
                                        supplierDropdownClassName || undefined
                                    }
                                    errors={
                                        showValidation ? supplierErr : undefined
                                    }
                                    renderLabel={renderSupplierLabel}
                                    allowAdditions={allowSupplierOther}
                                    additionLabel="Add "
                                    additionPosition="top"
                                    onAddItem={
                                        allowSupplierOther
                                            ? (_, { value }) => {
                                                  const stringValue = String(
                                                      value ?? '',
                                                  ).trim()
                                                  if (
                                                      !stringValue ||
                                                      stringValue.startsWith(
                                                          '__header_',
                                                      )
                                                  ) {
                                                      return
                                                  }
                                                  setAddedSupplierOptions(prev => {
                                                      if (
                                                          prev.some(
                                                              opt =>
                                                                  String(
                                                                      opt?.value ??
                                                                          '',
                                                                  ).trim() ===
                                                                  stringValue,
                                                          )
                                                      ) {
                                                          return prev
                                                      }
                                                      return [
                                                          ...prev,
                                                          {
                                                              key: `other-${stringValue}`,
                                                              value: stringValue,
                                                              text: stringValue,
                                                          },
                                                      ]
                                                  })
                                                  handleSupplierDropdownChange(_, {
                                                      value: stringValue,
                                                  })
                                              }
                                            : undefined
                                    }
                                    onChange={handleSupplierDropdownChange}
                                    />
                                    {showSupplierCustomValue ? (
                                        <SupplierDropdownValueLayer>
                                            <SupplierOptionDisplay
                                                meta={
                                                    selectedSupplierOption.meta
                                                }
                                            />
                                        </SupplierDropdownValueLayer>
                                    ) : null}
                                </SupplierDropdownFieldContainer>
                            </SupplierDropdownWrap>
                            {selectedSupplierOption?.meta ? (
                                <SelectedSupplierSummary>
                                    <SupplierOptionDisplay
                                        meta={selectedSupplierOption.meta}
                                        summary
                                    />
                                </SelectedSupplierSummary>
                            ) : null}
                        </ClaimInspectionForm>
                    </CardBody>
                </Card>

                <Card>
                    <SectionHeaderWithCopy
                        title="Customer Service Claim Portal"
                        sectionId={portalSectionId}
                        onCopySection={onCopySection}
                        readOnly={portalFieldsDisabled}
                        rightContent={
                            sectionWorkflowEnabled ? (
                                <ClaimSectionStatusBadge
                                    status={portalWorkflowStatus}
                                />
                            ) : null
                        }
                    />
                    <CardBody>
                        <ClaimSectionWorkflowBody status={portalWorkflowStatus}>
                        <ClaimInspectionForm>
                            <PortalFieldGrid>
                                <PortalMatType>
                                    <ClaimFieldWithInlineMedia
                                        question={matTypeQuestion}
                                        sectionId={portalSectionId}
                                        addMedia={addMedia}
                                        removeMedia={removeMedia}
                                        onSaveComment={getClaimMediaSaveComment?.(
                                            matTypeQuestion?.Id,
                                            portalSectionId,
                                        )}
                                        mediaDisabled={portalMediaDisabled}
                                        label={portalFieldLabels.matType}
                                        renderField={({ label: fieldLabel }) => (
                                            <ClaimPortalDropdownField
                                                control={portalControl}
                                                name="matType"
                                                label={fieldLabel}
                                                options={matTypeOptions}
                                                disabled={portalFieldsDisabled}
                                                question={matTypeQuestion}
                                                onValueChange={val =>
                                                    updatePortal('matType', val)
                                                }
                                            />
                                        )}
                                    />
                                </PortalMatType>
                                <PortalSize>
                                    <ClaimFieldWithInlineMedia
                                        question={sizeQuestion}
                                        sectionId={portalSectionId}
                                        addMedia={addMedia}
                                        removeMedia={removeMedia}
                                        onSaveComment={getClaimMediaSaveComment?.(
                                            sizeQuestion?.Id,
                                            portalSectionId,
                                        )}
                                        mediaDisabled={portalMediaDisabled}
                                        label={portalFieldLabels.size}
                                        renderField={({ label: fieldLabel }) => (
                                            <ClaimPortalDropdownField
                                                control={portalControl}
                                                name="size"
                                                label={fieldLabel}
                                                options={sizeOptions}
                                                disabled={portalFieldsDisabled}
                                                question={sizeQuestion}
                                                onValueChange={val =>
                                                    updatePortal('size', val)
                                                }
                                            />
                                        )}
                                    />
                                </PortalSize>
                                <PortalItemSketch>
                                    <ItemSketchFieldWrap>
                                        <FormFieldLabel>
                                            {portalFieldLabels.itemSketch}
                                        </FormFieldLabel>
                                        <ItemSketchRow>
                                            <SketchInputWrap>
                                                <ClaimInspectionDynamicField
                                                    question={itemSketchQuestion}
                                                    control={portalControl}
                                                    name="itemSketchNumber"
                                                    disabled={portalFieldsDisabled}
                                                    onChange={val =>
                                                        updatePortal(
                                                            'itemSketchNumber',
                                                            val,
                                                        )
                                                    }
                                                />
                                            </SketchInputWrap>
                                            {shouldShowClaimInlineMedia(
                                                itemSketchQuestion,
                                            ) ? (
                                                <div className="claim-inline-media-slot">
                                                    <ClaimInlineMediaActions
                                                        mediaQuestion={
                                                            itemSketchQuestion
                                                        }
                                                        sectionId={portalSectionId}
                                                        addMedia={addMedia}
                                                        removeMedia={removeMedia}
                                                        onSaveComment={getClaimMediaSaveComment?.(
                                                            itemSketchQuestionId,
                                                            portalSectionId,
                                                        )}
                                                        disabled={
                                                            portalMediaDisabled ||
                                                            !itemSketchQuestionId
                                                        }
                                                    />
                                                </div>
                                            ) : null}
                                        </ItemSketchRow>
                                    </ItemSketchFieldWrap>
                                </PortalItemSketch>
                                <PortalQuantity>
                                    <ClaimFieldWithInlineMedia
                                        question={quantityQuestion}
                                        sectionId={portalSectionId}
                                        addMedia={addMedia}
                                        removeMedia={removeMedia}
                                        onSaveComment={getClaimMediaSaveComment?.(
                                            quantityQuestion?.Id,
                                            portalSectionId,
                                        )}
                                        mediaDisabled={portalMediaDisabled}
                                        label={portalFieldLabels.quantity}
                                        renderField={({ label: fieldLabel }) => (
                                            <ClaimInspectionDynamicField
                                                question={quantityQuestion}
                                                control={portalControl}
                                                name="quantity"
                                                label={fieldLabel}
                                                disabled={portalFieldsDisabled}
                                                onChange={val =>
                                                    updatePortal('quantity', val)
                                                }
                                            />
                                        )}
                                    />
                                </PortalQuantity>
                                <PortalSfo>
                                    <ClaimFieldWithInlineMedia
                                        question={sfoOrderQuestion}
                                        sectionId={portalSectionId}
                                        addMedia={addMedia}
                                        removeMedia={removeMedia}
                                        onSaveComment={getClaimMediaSaveComment?.(
                                            sfoOrderQuestion?.Id,
                                            portalSectionId,
                                        )}
                                        mediaDisabled={portalMediaDisabled}
                                        label={portalFieldLabels.sfoOrder}
                                        renderField={({ label: fieldLabel }) => (
                                            <ClaimInspectionDynamicField
                                                question={sfoOrderQuestion}
                                                control={portalControl}
                                                name="sfoOrder"
                                                label={fieldLabel}
                                                disabled={portalFieldsDisabled}
                                                onChange={val =>
                                                    updatePortal('sfoOrder', val)
                                                }
                                            />
                                        )}
                                    />
                                </PortalSfo>
                                <PortalComplaint>
                                    {showComplaintDetailsMedia ? (
                                        <Form.Field>
                                            <label>
                                                {portalFieldLabels.complaintDetails}
                                            </label>
                                            <ComplaintMediaBlock>
                                                <ClaimMediaFileUpload
                                                    files={complaintMediaFiles}
                                                    onAddFiles={files =>
                                                        onComplaintMedia?.(files)
                                                    }
                                                    onRemoveFile={
                                                        onRemoveComplaintMedia
                                                    }
                                                    onSaveComment={
                                                        onComplaintMediaSaveComment
                                                    }
                                                    disabled={
                                                        portalMediaDisabled ||
                                                        !complaintDetailsQuestionId
                                                    }
                                                    canRemove={
                                                        !portalMediaDisabled &&
                                                        !!onRemoveComplaintMedia
                                                    }
                                                />
                                            </ComplaintMediaBlock>
                                            <ClaimInspectionDynamicField
                                                question={complaintDetailsQuestion}
                                                control={portalControl}
                                                name="complaintDetails"
                                                disabled={portalFieldsDisabled}
                                                mediaFiles={complaintMediaFiles}
                                                onMediaFiles={onComplaintMedia}
                                                onRemoveMediaFiles={
                                                    onRemoveComplaintMedia
                                                }
                                                onSaveComment={
                                                    onComplaintMediaSaveComment
                                                }
                                                mediaDisabled={
                                                    portalMediaDisabled ||
                                                    !complaintDetailsQuestionId
                                                }
                                                minHeight="120px"
                                                onChange={val =>
                                                    updatePortal(
                                                        'complaintDetails',
                                                        val,
                                                    )
                                                }
                                            />
                                        </Form.Field>
                                    ) : (
                                        <ClaimFieldWithInlineMedia
                                            question={complaintDetailsQuestion}
                                            sectionId={portalSectionId}
                                            addMedia={addMedia}
                                            removeMedia={removeMedia}
                                            onSaveComment={getClaimMediaSaveComment?.(
                                                complaintDetailsQuestion?.Id,
                                                portalSectionId,
                                            )}
                                            mediaDisabled={portalMediaDisabled}
                                            label={portalFieldLabels.complaintDetails}
                                            renderField={({ label: fieldLabel }) => (
                                                <ClaimInspectionDynamicField
                                                    question={
                                                        complaintDetailsQuestion
                                                    }
                                                    control={portalControl}
                                                    name="complaintDetails"
                                                    label={fieldLabel}
                                                    disabled={portalFieldsDisabled}
                                                    minHeight="120px"
                                                    onChange={val =>
                                                        updatePortal(
                                                            'complaintDetails',
                                                            val,
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                    )}
                                </PortalComplaint>
                            </PortalFieldGrid>
                            <PortalTailGrid>
                                <GridFieldCell>
                                    <ClaimFieldWithInlineMedia
                                        question={codeQuestion}
                                        sectionId={portalSectionId}
                                        addMedia={addMedia}
                                        removeMedia={removeMedia}
                                        onSaveComment={getClaimMediaSaveComment?.(
                                            codeQuestion?.Id,
                                            portalSectionId,
                                        )}
                                        mediaDisabled={portalMediaDisabled}
                                        label={portalFieldLabels.code}
                                        renderField={({ label: fieldLabel }) =>
                                            isClaimMultipleChoiceQuestion(
                                                codeQuestion,
                                            ) || codeOptions?.length > 0 ? (
                                                <ClaimPortalDropdownField
                                                    control={portalControl}
                                                    name="code"
                                                    label={fieldLabel}
                                                    options={codeOptions}
                                                    disabled={portalFieldsDisabled}
                                                    question={codeQuestion}
                                                    onValueChange={val =>
                                                        updatePortal('code', val)
                                                    }
                                                />
                                            ) : (
                                                <ClaimInspectionTextField
                                                    control={portalControl}
                                                    name="code"
                                                    label={fieldLabel}
                                                    placeholder="Enter text here"
                                                    disabled={portalFieldsDisabled}
                                                    onChange={val =>
                                                        updatePortal('code', val)
                                                    }
                                                />
                                            )
                                        }
                                    />
                                </GridFieldCell>
                                {rmaQuestion ? (
                                    <GridFieldCell>
                                        <ClaimFieldWithInlineMedia
                                            question={rmaQuestion}
                                            sectionId={portalSectionId}
                                            addMedia={addMedia}
                                            removeMedia={removeMedia}
                                            onSaveComment={getClaimMediaSaveComment?.(
                                                rmaQuestion?.Id,
                                                portalSectionId,
                                            )}
                                            mediaDisabled={portalMediaDisabled}
                                            label={portalFieldLabels.rma}
                                            renderField={({ label: fieldLabel }) => (
                                                <ClaimInspectionDynamicField
                                                    question={rmaQuestion}
                                                    control={portalControl}
                                                    name="rma"
                                                    label={fieldLabel}
                                                    disabled={portalFieldsDisabled}
                                                    onChange={val =>
                                                        updatePortal('rma', val)
                                                    }
                                                />
                                            )}
                                        />
                                    </GridFieldCell>
                                ) : null}
                                <GridFieldCell>
                                    <ClaimFieldWithInlineMedia
                                        question={portalCreditIssueQuestion}
                                        sectionId={portalSectionId}
                                        addMedia={addMedia}
                                        removeMedia={removeMedia}
                                        onSaveComment={getClaimMediaSaveComment?.(
                                            portalCreditIssueQuestion?.Id,
                                            portalSectionId,
                                        )}
                                        mediaDisabled={portalMediaDisabled}
                                        label={portalFieldLabels.creditIssue}
                                        renderField={({ label: fieldLabel }) => (
                                            <ClaimPortalDropdownField
                                                control={portalControl}
                                                name="creditIssue"
                                                label={fieldLabel}
                                                options={creditIssuePortalOptions}
                                                disabled={portalFieldsDisabled}
                                                question={portalCreditIssueQuestion}
                                                onValueChange={val =>
                                                    updatePortal(
                                                        'creditIssue',
                                                        val,
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                </GridFieldCell>
                                <PortalNotesCell>
                                    <ClaimNotesWithMediaField
                                        question={portalNotesQuestion}
                                        questionId={
                                            portalNotesQuestionId ||
                                            portalNotesQuestion?.Id
                                        }
                                        control={portalControl}
                                        name="notes"
                                        label={portalFieldLabels.notes}
                                        disabled={portalFieldsDisabled}
                                        blockMediaUpload={portalMediaDisabled}
                                        mediaFiles={portalNotesMediaFiles}
                                        onMediaFiles={onPortalNotesMedia}
                                        onRemoveMediaFiles={
                                            onRemovePortalNotesMedia
                                        }
                                        onSaveComment={
                                            onPortalNotesMediaSaveComment
                                        }
                                        onChange={val =>
                                            updatePortal('notes', val)
                                        }
                                    />
                                </PortalNotesCell>
                            <ClaimTemplateQuestionGrid
                                embedded
                                questions={portalExtraQuestions}
                                sectionId={portalSectionId}
                                startNumber={portalExtraStartNumber}
                                disabled={portalFieldsDisabled}
                                mediaDisabled={portalMediaDisabled}
                                templateData={templateData}
                                extraAnswers={extraAnswers}
                                onExtraAnswerChange={updateExtraAnswer}
                                addMedia={addMedia}
                                removeMedia={removeMedia}
                                getClaimMediaSaveComment={getClaimMediaSaveComment}
                            />
                            </PortalTailGrid>
                        </ClaimInspectionForm>
                        </ClaimSectionWorkflowBody>
                    </CardBody>
                    {sectionWorkflowEnabled ? (
                        <ClaimSectionWorkflowFooter
                            status={portalWorkflowStatus}
                            allStatuses={sectionWorkflowStatuses}
                            completing={sectionWorkflowBusy}
                            onComplete={openSectionCompleteModal}
                            onUncomplete={openSectionUncompleteModal}
                        />
                    ) : null}
                </Card>

                {genericClaimSections.map(section => (
                    <GenericClaimSectionCard
                        key={String(section?.SectionId ?? section?.SectionName)}
                        section={section}
                        readOnly={readOnly}
                        templatePreview={templatePreview}
                        sectionWorkflowEnabled={sectionWorkflowEnabled}
                        sectionWorkflowStatuses={sectionWorkflowStatuses}
                        sectionWorkflowBusy={sectionWorkflowBusy}
                        openSectionCompleteModal={openSectionCompleteModal}
                        openSectionUncompleteModal={openSectionUncompleteModal}
                        templateData={templateData}
                        extraAnswers={extraAnswers}
                        onExtraAnswerChange={updateExtraAnswer}
                        addMedia={addMedia}
                        removeMedia={removeMedia}
                        getClaimMediaSaveComment={getClaimMediaSaveComment}
                        onCopySection={onCopySection}
                    />
                ))}

                {qualitySectionId ? (
                <Card>
                    <SectionHeaderWithCopy
                        title="Quality"
                        sectionId={qualitySectionId}
                        onCopySection={onCopySection}
                        readOnly={qualityFieldsDisabled}
                        rightContent={
                            sectionWorkflowEnabled ? (
                                <ClaimSectionStatusBadge
                                    status={qualityWorkflowStatus}
                                />
                            ) : null
                        }
                    />
                    <CardBody>
                        <ClaimSectionWorkflowBody status={qualityWorkflowStatus}>
                        <ClaimInspectionForm>
                            <FieldGrid>
                                <GridFieldCell>
                                    <ClaimFieldWithInlineMedia
                                        question={receivingDateQuestion}
                                        sectionId={qualitySectionId}
                                        addMedia={addMedia}
                                        removeMedia={removeMedia}
                                        onSaveComment={getClaimMediaSaveComment?.(
                                            receivingDateQuestion?.Id,
                                            qualitySectionId,
                                        )}
                                        mediaDisabled={qualityMediaDisabled}
                                        label="1. Receiving Date"
                                        renderField={({ label: fieldLabel }) => (
                                            <ClaimInspectionDateField
                                                control={qualityControl}
                                                name="receivingDate"
                                                label={fieldLabel}
                                                placeholder="Select date and time"
                                                showTimeInput={receivingDateUsesTime}
                                                disabled={qualityFieldsDisabled}
                                                onChange={date =>
                                                    updateQuality(
                                                        'receivingDate',
                                                        date,
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                </GridFieldCell>
                                <GridFieldCell>
                                    <ClaimFieldWithInlineMedia
                                        question={carrierQuestion}
                                        sectionId={qualitySectionId}
                                        addMedia={addMedia}
                                        removeMedia={removeMedia}
                                        onSaveComment={getClaimMediaSaveComment?.(
                                            carrierQuestion?.Id,
                                            qualitySectionId,
                                        )}
                                        mediaDisabled={qualityMediaDisabled}
                                        label="2. Carrier"
                                        renderField={({ label: fieldLabel }) => (
                                            <ClaimPortalDropdownField
                                                control={qualityControl}
                                                name="carrier"
                                                label={fieldLabel}
                                                options={carrierOptions}
                                                disabled={qualityFieldsDisabled}
                                                question={carrierQuestion}
                                                onValueChange={val =>
                                                    updateQuality('carrier', val)
                                                }
                                            />
                                        )}
                                    />
                                </GridFieldCell>
                                <GridFieldCell>
                                    <ClaimFieldWithInlineMedia
                                        question={embossQuestion}
                                        sectionId={qualitySectionId}
                                        addMedia={addMedia}
                                        removeMedia={removeMedia}
                                        onSaveComment={getClaimMediaSaveComment?.(
                                            embossQuestion?.Id,
                                            qualitySectionId,
                                        )}
                                        mediaDisabled={qualityMediaDisabled}
                                        label="3. Emboss"
                                        renderField={({ label: fieldLabel }) => (
                                            <ClaimInspectionTextField
                                                control={qualityControl}
                                                name="emboss"
                                                label={fieldLabel}
                                                placeholder="Enter text here"
                                                disabled={qualityFieldsDisabled}
                                                onChange={val =>
                                                    updateQuality('emboss', val)
                                                }
                                            />
                                        )}
                                    />
                                </GridFieldCell>
                                <GridFieldCell>
                                    <ClaimFieldWithInlineMedia
                                        question={packingQuestion}
                                        sectionId={qualitySectionId}
                                        addMedia={addMedia}
                                        removeMedia={removeMedia}
                                        onSaveComment={getClaimMediaSaveComment?.(
                                            packingQuestion?.Id,
                                            qualitySectionId,
                                        )}
                                        mediaDisabled={qualityMediaDisabled}
                                        label="4. Packing / Mat Condition"
                                        renderField={({ label: fieldLabel }) => (
                                            <ClaimPortalDropdownField
                                                control={qualityControl}
                                                name="packingMatCondition"
                                                label={fieldLabel}
                                                options={packingOptions}
                                                disabled={qualityFieldsDisabled}
                                                question={packingQuestion}
                                                onValueChange={val =>
                                                    updateQuality(
                                                        'packingMatCondition',
                                                        val,
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                </GridFieldCell>
                                <GridFieldCell>
                                    <ClaimFieldWithInlineMedia
                                        question={dispositionQuestion}
                                        sectionId={qualitySectionId}
                                        addMedia={addMedia}
                                        removeMedia={removeMedia}
                                        onSaveComment={getClaimMediaSaveComment?.(
                                            dispositionQuestion?.Id,
                                            qualitySectionId,
                                        )}
                                        mediaDisabled={qualityMediaDisabled}
                                        label="5. Disposition"
                                        renderField={({ label: fieldLabel }) => (
                                            <ClaimPortalDropdownField
                                                control={qualityControl}
                                                name="disposition"
                                                label={fieldLabel}
                                                options={dispositionOptions}
                                                disabled={qualityFieldsDisabled}
                                                question={dispositionQuestion}
                                                onValueChange={val =>
                                                    updateQuality(
                                                        'disposition',
                                                        val,
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                </GridFieldCell>
                                <GridFieldCell>
                                    <ClaimFieldWithInlineMedia
                                        question={trackingQuestion}
                                        sectionId={qualitySectionId}
                                        addMedia={addMedia}
                                        removeMedia={removeMedia}
                                        onSaveComment={getClaimMediaSaveComment?.(
                                            trackingQuestion?.Id,
                                            qualitySectionId,
                                        )}
                                        mediaDisabled={qualityMediaDisabled}
                                        label="6. Tracking #"
                                        renderField={({ label: fieldLabel }) => (
                                            <ClaimInspectionTextField
                                                control={qualityControl}
                                                name="tracking"
                                                label={fieldLabel}
                                                placeholder="Enter text here"
                                                disabled={qualityFieldsDisabled}
                                                onChange={val =>
                                                    updateQuality('tracking', val)
                                                }
                                            />
                                        )}
                                    />
                                </GridFieldCell>
                                {statusQuestion ? (
                                    <GridFieldCell>
                                        <ClaimFieldWithInlineMedia
                                            question={statusQuestion}
                                            sectionId={qualitySectionId}
                                            addMedia={addMedia}
                                            removeMedia={removeMedia}
                                            onSaveComment={getClaimMediaSaveComment?.(
                                                statusQuestion?.Id,
                                                qualitySectionId,
                                            )}
                                            mediaDisabled={qualityMediaDisabled}
                                            label="7. Status"
                                            renderField={({ label: fieldLabel }) => (
                                                <ClaimPortalDropdownField
                                                    control={qualityControl}
                                                    name="status"
                                                    label={fieldLabel}
                                                    options={statusOptions}
                                                    disabled={qualityFieldsDisabled}
                                                    question={statusQuestion}
                                                    onValueChange={val =>
                                                        updateQuality(
                                                            'status',
                                                            val,
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                    </GridFieldCell>
                                ) : null}
                                <FullWidth>
                                    <ClaimNotesWithMediaField
                                        question={qualityNotesQuestion}
                                        questionId={qualityNotesQuestion?.Id}
                                        control={qualityControl}
                                        name="notes"
                                        label={
                                            statusQuestion
                                                ? '8. Notes'
                                                : '7. Notes'
                                        }
                                        disabled={qualityFieldsDisabled}
                                        blockMediaUpload={qualityMediaDisabled}
                                        mediaFiles={qualityNotesMediaFiles}
                                        onMediaFiles={onQualityNotesMedia}
                                        onRemoveMediaFiles={
                                            onRemoveQualityNotesMedia
                                        }
                                        onSaveComment={
                                            onQualityNotesMediaSaveComment
                                        }
                                        onChange={val =>
                                            updateQuality('notes', val)
                                        }
                                    />
                                </FullWidth>
                            </FieldGrid>
                            <ClaimTemplateQuestionGrid
                                questions={qualityExtraQuestions}
                                sectionId={qualitySectionId}
                                startNumber={qualityExtraStartNumber}
                                disabled={qualityFieldsDisabled}
                                mediaDisabled={qualityMediaDisabled}
                                templateData={templateData}
                                extraAnswers={extraAnswers}
                                onExtraAnswerChange={updateExtraAnswer}
                                addMedia={addMedia}
                                removeMedia={removeMedia}
                                getClaimMediaSaveComment={getClaimMediaSaveComment}
                            />
                        </ClaimInspectionForm>
                        </ClaimSectionWorkflowBody>
                    </CardBody>
                    {sectionWorkflowEnabled ? (
                        <ClaimSectionWorkflowFooter
                            status={qualityWorkflowStatus}
                            allStatuses={sectionWorkflowStatuses}
                            completing={sectionWorkflowBusy}
                            onComplete={openSectionCompleteModal}
                            onUncomplete={openSectionUncompleteModal}
                        />
                    ) : null}
                </Card>
                ) : null}

                {completionSectionId ? (
                <Card>
                    <SectionHeaderWithCopy
                        title="Customer Service Completion"
                        sectionId={completionSectionId}
                        onCopySection={onCopySection}
                        readOnly={completionFieldsDisabled}
                        rightContent={
                            sectionWorkflowEnabled ? (
                                <ClaimSectionStatusBadge
                                    status={completionWorkflowStatus}
                                />
                            ) : null
                        }
                    />
                    <CardBody>
                        <ClaimSectionWorkflowBody status={completionWorkflowStatus}>
                        <ClaimInspectionForm>
                            <SingleColumnFields>
                                <ClaimFieldWithInlineMedia
                                    question={completionCreditIssueQuestion}
                                    sectionId={completionSectionId}
                                    addMedia={addMedia}
                                    removeMedia={removeMedia}
                                    onSaveComment={getClaimMediaSaveComment?.(
                                        completionCreditIssueQuestion?.Id,
                                        completionSectionId,
                                    )}
                                    mediaDisabled={completionMediaDisabled}
                                    label="1. Credit Issue"
                                    renderField={({ label: fieldLabel }) => (
                                        <ClaimPortalDropdownField
                                            control={completionControl}
                                            name="creditIssue"
                                            label={fieldLabel}
                                            options={creditIssueCompletionOptions}
                                            disabled={completionFieldsDisabled}
                                            question={completionCreditIssueQuestion}
                                            onValueChange={val =>
                                                updateCompletion(
                                                    'creditIssue',
                                                    val,
                                                )
                                            }
                                        />
                                    )}
                                />
                                <ClaimNotesWithMediaField
                                    question={completionNotesQuestion}
                                    questionId={completionNotesQuestion?.Id}
                                    control={completionControl}
                                    name="notes"
                                    label="2. Notes"
                                    disabled={completionFieldsDisabled}
                                    blockMediaUpload={completionMediaDisabled}
                                    mediaFiles={completionNotesMediaFiles}
                                    onMediaFiles={onCompletionNotesMedia}
                                    onRemoveMediaFiles={
                                        onRemoveCompletionNotesMedia
                                    }
                                    onSaveComment={
                                        onCompletionNotesMediaSaveComment
                                    }
                                    onChange={val =>
                                        updateCompletion('notes', val)
                                    }
                                />
                            </SingleColumnFields>
                            {completionExtraQuestions.length > 0 ? (
                            <ClaimTemplateQuestionGrid
                                questions={completionExtraQuestions}
                                sectionId={completionSectionId}
                                startNumber={completionExtraStartNumber}
                                disabled={completionFieldsDisabled}
                                mediaDisabled={completionMediaDisabled}
                                templateData={templateData}
                                extraAnswers={extraAnswers}
                                onExtraAnswerChange={updateExtraAnswer}
                                addMedia={addMedia}
                                removeMedia={removeMedia}
                                getClaimMediaSaveComment={getClaimMediaSaveComment}
                            />
                            ) : null}
                        </ClaimInspectionForm>
                        </ClaimSectionWorkflowBody>
                    </CardBody>
                    {sectionWorkflowEnabled ? (
                        <ClaimSectionWorkflowFooter
                            status={completionWorkflowStatus}
                            allStatuses={sectionWorkflowStatuses}
                            completing={sectionWorkflowBusy}
                            onComplete={openSectionCompleteModal}
                            onUncomplete={openSectionUncompleteModal}
                        />
                    ) : null}
                </Card>
                ) : null}
            </Content>

            {sectionWorkflowModal}
        </PageWrap>
    )
}
